// ========================================================================
// text.js — text(): words on the stage: pick, cycle, shuffle, scroll, ticker, reveal, data
// ========================================================================

import { truthy } from "../parser/cueUtils.js";
import { dataLine } from "./textData.js";
import { maskFromArgs, outlinesOf, randomPointIn, chordAt, outlineBox } from "../system/stageMask.js";
import { setAlong } from "../system/textAlong.js";
import { bindParam, isSignalRef, isOscRef, isMoving, releaseBinding } from '../control/paramBinding.js';
import { evalMaybeRandom, makeValueGenerator } from './audio/audioShared.js';
import { runNext } from './cueNext.js';
import { set as busSet } from '../control/paramBus.js';
import { secs, secPerBeat } from './beats.js';
import { atPair } from '../system/viewBand.js';
import { csvItems } from './csvText.js';
import { stageFlash } from '../system/screenFx.js';
import { pulse } from '../control/liveSignals.js';
import { blendOf } from '../system/vizStyle.js';
import { fadeTrail } from '../system/scopeViews.js';
import { shuffle as shuffleItems } from "./audio/patterns.js";
import { splitContent } from "./textSplit.js";
import { onReset, ownerSignal } from "../system/teardown.js";
import { findFile, missNote } from "../system/mediaFolder.js";

// Which line a text is showing, published as <uid>.line on the parameter bus:
// 1, 2, 3 … as each appears, 0 before the first and after the last. Anything
// can follow a text with it — trig:"story.line==5" fires a cue as line 5
// appears (route.js arms those for the length of a stay), and OSC or a
// follow() can read it too.
function noteLine(uid, n) {
  try { busSet(`${uid}.line`, n, { source: "text" }); } catch { /* no bus */ }
}
// <uid>.end: 0 as a text starts, 1 when it has run to its end (not when it
// is stopped). A map-mode station can wait for it: until:"story.end".
function noteEnd(uid, v) {
  try { busSet(`${uid}.end`, v, { source: "text" }); } catch { /* no bus */ }
}

/* ------------------------------------------------------------------------
   1) Core Helpers
-------------------------------------------------------------------------*/

/**
 * A scroll speed, with follow() allowed. Sampled at trigger, like fade() and
 * ui(): the scroll is one animation whose duration is computed from
 * distance/speed when it starts, so a later value has nothing to change.
 *
 * Number(...) alone reads a follow() as NaN, which would silently become the
 * 60 px/s default.
 */
function textSpeed(value, fallback = 60) {
  if (!isSignalRef(value) && !isOscRef(value)) return Number(value) || fallback;
  const b = bindParam(value, () => {}, { min: 10, max: 400 });
  b?.unbind?.();
  return Number.isFinite(b?.value) && b.value > 0 ? b.value : fallback;
}

// The project's default font for text cues (Preferences → Fonts). Goes in
// before a cue's own style:, so a font-family there still wins.
function defaultFontCss() {
  return window.textFont ? `font-family:${window.textFont};` : "";
}

function unquote(v) {
  if (typeof v !== "string") return v;
  return v.replace(/^[`'"]+|[`'"]+$/g, "");
}


function rafWait(ms, token) {
  return new Promise((resolve) => {
    if (token?.cancel) return resolve();
    const end = performance.now() + ms;
    function loop(t) {
      if (token?.cancel) return resolve();
      if (t >= end) return resolve();
      requestAnimationFrame(loop);
    }
    requestAnimationFrame(loop);
  });
}

/**
 * Show a custom confirmation dialog
 * @param {string} message - The message to display
 * @param {string} confirmText - Text for confirm button (default: "Yes")
 * @param {string} cancelText - Text for cancel button (default: "No")
 * @returns {Promise<boolean>} - Resolves true if confirmed, false if cancelled
 */
function showConfirmDialog(message, confirmText = "Yes", cancelText = "No") {
  return new Promise((resolve) => {
    // Create overlay
    const overlay = document.createElement("div");
    overlay.style.cssText = `
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.7);
      z-index: 9999999;
      display: flex;
      align-items: center;
      justify-content: center;
      font-family: system-ui, -apple-system, sans-serif;
    `;

    // Create dialog
    const dialog = document.createElement("div");
    dialog.style.cssText = `
      background: #222;
      border: 1px solid #444;
      border-radius: 8px;
      padding: 24px 32px;
      text-align: center;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
      max-width: 300px;
    `;

    // Message
    const msg = document.createElement("div");
    msg.textContent = message;
    msg.style.cssText = `
      color: white;
      font-size: 1.1em;
      margin-bottom: 20px;
    `;

    // Buttons container
    const buttons = document.createElement("div");
    buttons.style.cssText = `
      display: flex;
      gap: 12px;
      justify-content: center;
    `;

    // Confirm button
    const confirmBtn = document.createElement("button");
    confirmBtn.textContent = confirmText;
    confirmBtn.style.cssText = `
      background: #e74c3c;
      color: white;
      border: none;
      border-radius: 4px;
      padding: 8px 20px;
      font-size: 1em;
      cursor: pointer;
      transition: background 0.2s;
    `;
    confirmBtn.onmouseenter = () => confirmBtn.style.background = "#c0392b";
    confirmBtn.onmouseleave = () => confirmBtn.style.background = "#e74c3c";

    // Cancel button
    const cancelBtn = document.createElement("button");
    cancelBtn.textContent = cancelText;
    cancelBtn.style.cssText = `
      background: #555;
      color: white;
      border: none;
      border-radius: 4px;
      padding: 8px 20px;
      font-size: 1em;
      cursor: pointer;
      transition: background 0.2s;
    `;
    cancelBtn.onmouseenter = () => cancelBtn.style.background = "#666";
    cancelBtn.onmouseleave = () => cancelBtn.style.background = "#555";

    // Wire up events
    const cleanup = (result) => {
      overlay.remove();
      resolve(result);
    };

    confirmBtn.onclick = () => cleanup(true);
    cancelBtn.onclick = () => cleanup(false);
    overlay.onclick = (e) => {
      if (e.target === overlay) cleanup(false);
    };

    // Assemble
    buttons.appendChild(confirmBtn);
    buttons.appendChild(cancelBtn);
    dialog.appendChild(msg);
    dialog.appendChild(buttons);
    overlay.appendChild(dialog);
    document.body.appendChild(overlay);

    // Focus confirm button
    confirmBtn.focus();
  });
}

/* ------------------------------------------------------------------------
   2) Parameter Parsing
-------------------------------------------------------------------------*/

function parseTextParams(ast, cueElement) {
  const params = {};
  for (const p of (ast?.args || [])) {
    params[p.type] = p.value;
  }

  // data:bin | hex | dec | float -- lines of digits (textData.js): a cycle of
  // them (a new one each dur:, a quarter beat by default, or each trig: hit),
  // or one long one crawling past with ticker:1; monospace, unless styled
  if (params.data != null) {
    const kind = String(unquote(params.data)).toLowerCase();
    const len = Math.max(1, Math.min(400, Math.round(Number(params.length) || 32)));
    if (params.ticker != null) params.ticker = dataLine(kind, len * 16);
    else { params.cycle = Array.from({ length: 64 }, () => dataLine(kind, len)); params.dur ??= 0.25; }
    params.style = `font-family:'Iosevka Term', 'IBM Plex Mono', ui-monospace, monospace; ${params.style != null ? unquote(params.style) : ""}`;
  }

  // Detect mode and content
  let mode = null;
  let content = null;

  if (params.pick) {
    mode = "pick";
    content = params.pick;
  } else if (params.cycle) {
    mode = "cycle";
    content = params.cycle;
  } else if (params.shuffle) {
    mode = "shuffle";
    content = params.shuffle;
  } else if (params.scroll) {
    mode = "scroll";
    content = params.scroll;
  } else if (params.ticker) {
    mode = "ticker";
    content = params.ticker;
  } else if (params.reveal) {
    mode = "reveal";
    content = params.reveal;
  }

  // Unit splitting mode
  const unit = unquote(params.unit || params.mode || "line");

  // Timing: dur:, fade:, gap:, linger:, fadeout: and a line's own "3:" are
  // beats, turned to seconds here (fade: is written in ms: the same scaling).
  // The defaults are seconds. speed: is a rate, not a time.
  const k = secPerBeat();
  const dur = Number(secs(params.dur, k)) || 2;
  const speed = textSpeed(params.speed);    // px/sec for scroll mode
  // fade: as written; left out, none -- a text is plain unless it asks (no fade, shadow or box)
  const fadeGiven = params.fade != null && Number.isFinite(Number(secs(params.fade, k)));
  const fadeMs = fadeGiven ? Math.max(0, Number(secs(params.fade, k))) : 0;
  // zoom:<times> / blur:<px> -- each item arrives from that big / that blurred (ARRIVING)
  const zoomIn = Number(params.zoom) > 0 && Number(params.zoom) !== 1 ? Number(params.zoom) : null;
  const blurIn = Number(params.blur) > 0 ? Number(params.blur) : null;
  // span:<beats> -- the time a list in the list (pick:[["BRO", "WHAT"]]) is
  // said in; by default a beat, or dur: when that is longer
  const span = params.span != null && Number(params.span) > 0 ? Number(secs(params.span, k)) : null;

  // Positioning
  const targetId = unquote(params.target || "center");
  const offsetX = Number(params.offsetX || 0);
  const offsetY = Number(params.offsetY || 0);
  const pad = Number(params.pad || 16); // Padding for target:self scroll mode

  // Style
  const style = unquote(params.style || "");

  // Identity
  const uid = String(params.uid || cueElement?.id || `cueText_${Date.now()}`);

  // Trigger mode: playhead (default), click, toggle, or extent
  // - playhead: triggers when playhead crosses element
  // - click: triggers on click
  // - toggle: click to start, click again to stop
  // - extent: runs while playhead is within element bounds
  const trig = unquote(params.trig || "playhead");

  // Sync random choice across clients (for pick mode)
  const sync = truthy(params.sync);

  // Ticker direction: left (default, text moves left) or right
  const direction = unquote(params.direction || params.dir || "left");

  // Hold: keep final content visible after completion
  const hold = truthy(params.hold);

  // hide:1 — hide the cue/target element while its text shows. Off by default:
  // a hidden element can no longer be seen or picked in the DSL inspector.
  const hide = [true, "true", 1, "1"].includes(params.hide);
  if (hide) hideUids.add(uid); else hideUids.delete(uid);

  // Background of the target:self teleprompter box (default: dark panel)
  const bg = unquote(params.bg || "");
  // flash:white / flash:invert -- the whole screen that colour (or inverted)
  // while each word shows: a strobe in time with the words themselves
  // a .csv: which column (col:name or col:2), or several (format:"{name}, {age}");
  // header:0 -- its first row is a row like the others
  const col = params.col != null ? unquote(String(params.col)) : null;
  const format = params.format != null ? unquote(String(params.format)) : null;
  const header = !["0", "false", "no", "off"].includes(String(params.header ?? "1").toLowerCase());
  // run by a signal (trig:beat.div(4)): cycle and shuffle step one item a hit
  const stepped = params._fromTrig === true;
  const flash = params.flash != null && !["0", "off", "false", "none"].includes(String(params.flash)) ? unquote(String(params.flash)) : null;
  // say:1 / say:<voice> -- each word spoken as it shows (say.js), and up
  // until the voice is done; sayRate: how fast
  const say = params.say == null || ["0", "off", "false", "none"].includes(String(params.say)) ? null
    : ([true, 1, "1", "true"].includes(params.say) ? true : unquote(String(params.say)));
  const sayRate = params.sayRate != null && Number(params.sayRate) > 0 ? Number(params.sayRate) : null;
  // from:<n> -- a cycle starts at its nth item (1 the first) and goes round
  // from there: several texts reading one file, each from its own place
  const from = Number.isFinite(Number(params.from)) && Number(params.from) > 1 ? Math.floor(Number(params.from)) : null;
  // to:<n> -- with from:, only those items, in order: from:3, to:3 the third alone,
  // from:3, to:7 the third to the seventh (a cycle goes round those)
  const to = Number.isFinite(Number(params.to)) && Number(params.to) >= 1 ? Math.floor(Number(params.to)) : null;
  const range = to != null ? [Number(params.from) >= 1 ? Math.floor(Number(params.from)) : 1, to] : null;
  // stay:1 -- every item stays where it landed (see STAY); at:rand -- each
  // somewhere new on the screen, at:[x, y] -- there (0..1 of the window)
  const stay = [true, "true", 1, "1"].includes(params.stay);
  // keep:8 -- with stay:1, each stays 8 beats where it landed, then fades out over its fade:
  const keepS = params.keep != null ? Number(secs(params.keep, k)) : NaN;
  const keepMs = Number.isFinite(keepS) && keepS > 0 ? keepS * 1000 : null;
  const atp = atPair(params.at);   // at:0.5 is at:[0.5, 0.5]
  const at = Array.isArray(atp) ? atp.map(Number) : params.at != null ? unquote(String(params.at)) : null;
  const scatter = typeof at === "string" && /^(rand|random|scatter)$/i.test(at);
  // at:flow -- each under the one before, as a page reads (see flowPlace)
  const flow = typeof at === "string" && /^flow$/i.test(at);
  // at:rows -- each a row down from the last one, wherever across (see rowPlace)
  const rows = typeof at === "string" && /^rows?$/i.test(at);
  // at:spot -- each somewhere inside the shape spot, where it is then (see insidePlace);
  // in:spot, at:mid / rows / flow / rand -- inside it, that way
  const shapeAt = typeof at === "string" && !scatter && !flow && !rows && !/^(mid|middle|center|centre)$/i.test(at) && /^[A-Za-z_][\w-]*$/.test(at) ? at : null;
  const inside = params.in != null ? unquote(String(params.in)) : shapeAt;
  const insideMode = params.in == null ? "rand"
    : typeof at === "string" && /^(mid|middle|center|centre)$/i.test(at) ? "mid"
    : rows ? "rows" : flow ? "flow" : "rand";
  // blend:difference -- the words mixed with what is under them (a look, as viz() sets)
  if (params.blend != null && blendOf(params.blend) != null) lookText(uid, { blend: blendOf(params.blend) });

  // reveal mode: how long the finished block stays up, then its fade-out length
  const linger = params.linger != null ? Number(secs(params.linger, k)) : 8;
  const fadeOut = params.fadeout != null ? Number(secs(params.fadeout, k)) : 3;
  // reveal at an element: block width in the element's own score units
  const width = params.width != null ? Number(params.width) : 300;
  // wrap:0 — lay the text out exactly as it is written. Long lines run on
  // instead of folding into the box, and a .txt file's own line breaks and
  // indentation are what you see. Anything else (or nothing) wraps.
  const wrap = !["0", "false", "no", "off"].includes(String(params.wrap ?? "").toLowerCase());
  // reveal at an element: host:<id> draws the block into that group instead of
  // the element's own — placed where the element is when the reveal starts,
  // then carried by the host. E.g. an object on a turning backdrop with
  // host:backdrop: the text starts beside it but rides the backdrop
  // without turning.
  const host = params.host != null ? unquote(String(params.host)) : null;
  // reveal at an element: screen:1 — the block starts at the element, then
  // keeps its place and size on the screen, whatever the view does (a zoom
  // right in and out again, a pan, a pinned map sliding): it stays readable
  const screen = [true, "true", 1, "1"].includes(params.screen);
  // reveal at an element: left:<0-1> — the block's left edge at this fraction
  // of the view's width (instead of offsetX from the element), so it lands
  // on screen whatever the window size; offsetY still places it vertically
  const left = params.left != null && Number.isFinite(Number(params.left)) ? Number(params.left) : null;

  return {
    host,
    left,
    screen,
    mode,
    content,
    unit,
    dur,
    span,
    flash,
    say,
    sayRate,
    from: range ? null : from,
    range,
    col,
    format,
    header,
    stepped,
    stay,
    keepMs,
    at,
    scatter,
    flow,
    rows,
    inside,
    insideMode,
    carry: params.in != null,
    zoomIn,
    blurIn,
    speed,
    fadeMs,
    fadeGiven,
    targetId,
    offsetX,
    offsetY,
    pad,
    style,
    uid,
    size: params.size,          // each item's own (wordLook), as written: rand(18, 60), Pwhite(...), follow(...)
    weight: params.weight,
    blur: params.blur,
    loop: params.loop,
    gap: secs(params.gap, k),
    k,                  // seconds per beat, for the content's own "3:" times
    trig,
    sync,
    direction,
    hold,
    hide,
    bg,
    linger,
    fadeOut,
    width,
    wrap,
    // cue to run when the text has finished (see cueNext.js)
    next: params.next ?? null,
  };
}

/* ------------------------------------------------------------------------
   3) Content Loading
-------------------------------------------------------------------------*/


// A file fetched for the text cues, kept a little while: a trig:beat.div(4)
// line runs the cue sixteen times a bar and need not fetch it each time
const fileCache = new Map();          // path -> { at, text }
const FILE_KEEP_MS = 10000;

/** A text file: a folder picked on this computer (mediaFolder.js), the project's own text/, then shared/text/. null: missing. */
async function fetchTextFile(str) {
  const hit = fileCache.get(str);
  if (hit && performance.now() - hit.at < FILE_KEEP_MS) return hit.text;
  const local = str.startsWith("/") ? null : findFile(str, "text");
  if (local) {
    const text = await local.text();
    fileCache.set(str, { at: performance.now(), text });
    return text;
  }
  const sharedPath = `${window.sharedDir || "shared/"}text/${str}`;
  let filePath = str.startsWith("/") ? str : sharedPath;
  // The project's own text/ folder wins over shared/
  if (!str.startsWith("/") && window.textDir) {
    try {
      const head = await fetch(`${window.textDir}${str}`, { method: "HEAD" });
      if (head.ok) filePath = `${window.textDir}${str}`;
    } catch { /* offline or blocked: fall back to shared/ */ }
  }
  try {
    const resp = await fetch(filePath);
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const text = await resp.text();
    fileCache.set(str, { at: performance.now(), text });
    return text;
  } catch {
    return null;
  }
}

/** loop:: 0 or inf for ever, else how many times (once when not given). */
const loopCountOf = loop => (loop === 0 || loop === "0" || loop === "inf") ? Infinity : (loop ? parseInt(loop) || 1 : 1);

/** What loadContent takes of a text's params. */
const loadOptsOf = p => ({ wrap: p.wrap, k: p.k, col: p.col, format: p.format, header: p.header, range: p.range });

/** The items of a text, only from:..to: when it gives to: (parseTextParams range). */
async function loadContent(content, unit, opts = {}) {
  const items = await loadAll(content, unit, opts);
  if (!opts.range) return items;
  const [a, b] = opts.range;
  return items.slice(Math.max(0, a - 1), Math.max(a, b));
}

async function loadAll(content, unit, opts = {}) {
  // Array content - convert to item objects
  if (Array.isArray(content)) {
    const k = opts.k ?? secPerBeat();
    return content.map(item => {
      // a list in the list -- ["BRO", "WHAT"] -- said in the time of one
      if (Array.isArray(item)) {
        const words = item.flat(Infinity).map(w => unquote(String(w)));
        return { text: words.join(" "), words, dur: null };
      }
      const str = unquote(String(item));
      // Check for duration prefix like "3:Hello" (beats)
      const match = str.match(/^(\d+(?:\.\d+)?):(.+)$/);
      if (match) {
        return { text: match[2], dur: Number(match[1]) * k };
      }
      return { text: str, dur: null };
    });
  }

  // String content
  const str = unquote(String(content));

  // File loading: a .txt split as unit: says, a .csv a row an item
  if (/\.(txt|csv)$/i.test(str)) {
    const data = await fetchTextFile(str);
    if (data == null) {
      console.warn(`[text] missing file: ${str}${missNote(str, "text")}`);
      return [{ text: `[Missing file: ${str}]`, dur: null }];
    }
    if (/\.csv$/i.test(str)) return csvItems(data, opts);
    return splitContent(data, unit, opts);
  }

  // Inline content
  return splitContent(str, unit, opts);
}

/* ------------------------------------------------------------------------
   4) Display Helpers
-------------------------------------------------------------------------*/

// Track hidden elements for restoration
const hiddenElements = new Map();
// uids whose cue asked for hide:1 (see parse)
const hideUids = new Set();

function hideCueElement(cueElement, uid) {
  if (!cueElement || !hideUids.has(uid)) return;
  // Store original opacity/visibility
  const original = {
    opacity: cueElement.style.opacity,
    visibility: cueElement.style.visibility,
  };
  hiddenElements.set(uid, { element: cueElement, original });
  // Hide with fade
  cueElement.style.transition = "opacity 200ms ease";
  cueElement.style.opacity = "0";
}

function showCueElement(uid) {
  const entry = hiddenElements.get(uid);
  if (!entry) return;
  const { element, original } = entry;
  // Restore with fade
  element.style.transition = "opacity 200ms ease";
  element.style.opacity = original.opacity || "";
  element.style.visibility = original.visibility || "";
  hiddenElements.delete(uid);
}

// =================================================================
//  WHERE A CENTRED TEXT STANDS
//  A text with no target stands in the middle of the screen. Dragged
//  there, or put there by viz(uid, at:[x, y]), it stands at that place --
//  fractions of the window -- and every item it shows after (a shuffle is
//  a new overlay per letter) and every run of it again stands there too.
// =================================================================

const textPlaces = new Map();          // uid -> { x, y } (0..1 of the window)
const textLooks = new Map();           // uid -> { color, opacity } from viz()

/** viz(uid, fill:, opacity:) on a text: kept, so every new item wears it. */
export function lookText(uid, look) {
  const now = { ...(textLooks.get(uid) || {}), ...look };
  textLooks.set(uid, now);
  for (const el of document.querySelectorAll(`.cue-text-overlay[data-uid="${CSS.escape(uid)}"]`)) wearLook(el, now);
}

function wearLook(el, look) {
  if (look?.color != null) el.style.color = look.color;
  if (look?.opacity != null) { el._lookOpacity = look.opacity; paintFilter(el); }
  if (look?.blend != null) el.style.mixBlendMode = look.blend;
}

/**
 * An overlay's filter: viz()'s effects (warp:, hue: ... boxFx.js), its blur
 * while it arrives (blur:), and viz()'s opacity. Written as --oscilla-fx, which
 * the stylesheet puts in front of what the stage needs (livecode.css; on Live's
 * dark stage the invert turned back, live.css): an inline filter took its place,
 * and white words went black.
 */
function paintFilter(el) {
  const parts = [];
  const fx = textFx.get(el.dataset.uid);
  if (fx) parts.push(fx);
  if (el._blur != null) parts.push(`blur(${el._blur}px)`);
  if (el._lookOpacity != null) parts.push(`opacity(${el._lookOpacity})`);
  if (parts.length) el.style.setProperty("--oscilla-fx", parts.join(" "));
  else el.style.removeProperty("--oscilla-fx");
}

// viz(voices, warp:20, hue:90 ...) -- screen()'s effects on a text: every word
// of the uid now and to come, the words left to stay, a scroll's or a ticker's box
const textFx = new Map();              // uid -> the CSS filter (boxFx.js chain)
const textFxKeys = new Map();          // uid -> boxFx's key for it

/** boxFx's key for the text `uid` (setBoxFx(key, ...)): kept while effects are on. */
export function textFxKey(uid) {
  let key = textFxKeys.get(uid);
  if (!key) textFxKeys.set(uid, key = { id: `text-${uid}`, isConnected: true });
  return key;
}

/** What viz()'s effects go on: the words first (their size sets the warp's margin). */
export function textFxTargets(uid) {
  const u = CSS.escape(uid);
  return [...document.querySelectorAll(`.cue-text-overlay[data-uid="${u}"], .cue-text-scroll-container[data-uid="${u}"], .cue-text-ticker-container[data-uid="${u}"], .cue-text-stay[data-uid="${u}"], .cue-text-trail[data-uid="${u}"]`)];
}

/** The filter for the text `uid` ("" none): on what is there, and kept for what comes. */
export function wearTextFx(uid, filter) {
  if (filter) textFx.set(uid, filter); else textFx.delete(uid);
  for (const el of textFxTargets(uid)) paintFilter(el);
}

// =================================================================
//  ARRIVING: zoom: AND blur:
//  zoom:12 -- each item starts 12 times its size and comes down to it over
//  fade:, as if from behind us to in front; blur:8 -- out of focus by 8 px,
//  sharpening as it lands. Round its own place: with at:flow / at:rand each
//  flies into where it lands. It is seen at once (in over the first part of
//  fade:) and big for a while before it eases down: faded in over the whole
//  fade:, the zoom was over before it could be seen.
// =================================================================

const ARRIVE_EASE = "cubic-bezier(0.45, 0, 0.25, 1)";
const ARRIVE_SEEN = 0.2;          // of fade:, to be seen

/** Before the overlay is first drawn: big, blurred, the transitions set. */
function startArrival(params, el) {
  const { zoomIn, fadeMs } = params;
  const blurIn = el._wordBlur ?? params.blurIn;     // blur:Pwhite(1, 99): each its own (wordLook)
  if (!zoomIn && !blurIn) return;
  const opacity = `opacity ${Math.round(fadeMs * ARRIVE_SEEN)}ms ease-out`;
  // big and blurred at once -- not a transition up to it: placed (at:flow,
  // at:rand) it has a style already, and going there would be a transition
  // of its own, still at the start when arrive() turns it round
  el.style.transition = opacity;
  if (zoomIn) el.style.scale = String(zoomIn);
  if (blurIn) { el._blur = blurIn; paintFilter(el); }
  void getComputedStyle(el).scale;          // taken as the start, now
  el.style.transition = [
    opacity,
    zoomIn ? `scale ${fadeMs}ms ${ARRIVE_EASE}` : null,
    blurIn ? `filter ${fadeMs}ms ${ARRIVE_EASE}` : null,
  ].filter(Boolean).join(", ");
}

/** A frame on: to its own size, in focus. */
function arrive(el) {
  if (el.style.scale) el.style.scale = "1";
  if (el._blur) { el._blur = 0; paintFilter(el); }
}

/** Put the text `uid` at x, y (0..1 of the window): now and from now on. */
export function placeText(uid, x, y) {
  textPlaces.set(uid, { x, y });
  for (const el of document.querySelectorAll(`.cue-text-overlay[data-uid="${CSS.escape(uid)}"]`)) {
    el.style.left = `${x * 100}%`;
    el.style.top = `${y * 100}%`;
  }
}

/** A centred text can be dragged; a click (no move) still does what it did. */
function makeDraggable(el, uid) {
  el.addEventListener("pointerdown", (e) => {
    if (e.button !== 0) return;
    const r = el.getBoundingClientRect();
    const sx = e.clientX, sy = e.clientY, cx = r.left + r.width / 2, cy = r.top + r.height / 2;
    let moved = false;
    const move = (ev) => {
      const dx = ev.clientX - sx, dy = ev.clientY - sy;
      if (!moved && Math.hypot(dx, dy) < 4) return;
      moved = true;
      placeText(uid, (cx + dx) / window.innerWidth, (cy + dy) / window.innerHeight);
    };
    const up = () => {
      window.removeEventListener("pointermove", move, true);
      window.removeEventListener("pointerup", up, true);
      // a drag is not a click
      if (moved) el.addEventListener("click", ev => ev.stopPropagation(), { capture: true, once: true });
    };
    window.addEventListener("pointermove", move, true);
    window.addEventListener("pointerup", up, true);
  });
}

// =================================================================
//  EACH ITEM ITS OWN SIZE, WEIGHT AND BLUR: size:, weight:, blur:
//  size:rand(18, 60) -- each word a size of its own, in px (a number: all
//  that size); weight:choose(300, 900); blur:Pwhite(1, 99, inf) -- a pattern
//  steps once a word (blur: is how blurred each arrives); size:follow(mouse.y,
//  18, 60) -- the size the signal is at as the word comes (follow(mouse.y):
//  8 to 96 px). Over style: and a style() preset; a word that stays is painted
//  that size.
// =================================================================

const WORD_LOOK = { size: ["font-size", 8, 96], weight: ["font-weight", 100, 900], blur: [null, 0, 60] };
const wordLooks = new Map();           // uid -> { size: { value } | { now } | { gen } ... }

/** A text run: its size:, weight: and blur: kept for each item it shows; a follow bound. */
function setWordLook(uid, params) {
  const look = {};
  for (const [key, [, min, max]] of Object.entries(WORD_LOOK)) {
    const owner = `text-${key}:${uid}`;
    const v = params[key];
    releaseBinding(owner);
    // a plain number of blur: is the text's own (blurIn): nothing to choose
    if (v == null || (key === "blur" && typeof v !== "object")) continue;
    if (isMoving(v)) {
      const spec = look[key] = { now: null };
      bindParam(v, x => { spec.now = x; }, { owner, min, max, default: (min + max) / 2 });
    } else if (v?.type === "pattern") look[key] = { pattern: v, gen: makeValueGenerator(v) };
    else look[key] = { value: v };
  }
  if (Object.keys(look).length) wordLooks.set(uid, look); else wordLooks.delete(uid);
}

/** The next value of a spec: a follow where it is, a pattern's next step, a fresh roll. */
function nextOf(spec) {
  if (spec.now != null) return spec.now;
  if (spec.pattern) {
    let v = spec.gen?.next();
    if (v == null) { spec.gen = makeValueGenerator(spec.pattern); v = spec.gen?.next(); }   // ended: round again
    return evalMaybeRandom(v);
  }
  return evalMaybeRandom(Array.isArray(spec.value) ? { type: "funcCall", name: "choose", args: spec.value } : spec.value);
}

/** One item's size, weight and blur, chosen as it comes. */
function wearWordLook(el, uid) {
  const look = wordLooks.get(uid);
  if (!look) return;
  for (const [key, [prop, , max]] of Object.entries(WORD_LOOK)) {
    const spec = look[key];
    if (!spec) continue;
    const v = unquote(String(nextOf(spec) ?? ""));
    if (v === "") continue;
    const n = Number(v);
    if (key === "blur") { if (n > 0) el._wordBlur = Math.min(500, n); continue; }   // max: a bare follow's range only
    // a number: px (a size) or 100..900 (a weight); "2em", "bold" as they are
    const css = Number.isFinite(n) ? (key === "size" ? `${Math.min(max * 4, Math.max(1, n))}px` : String(Math.round(Math.min(1000, Math.max(1, n))))) : v;
    el.style.setProperty(prop, css, "important");
  }
}

function createTextOverlay(text, uid, style, fadeMs, wrap = true) {
  const el = document.createElement("div");
  el.className = "cue-text-overlay";
  el.dataset.uid = uid;
  el.textContent = text;
  makeDraggable(el, uid);
  queueMicrotask(() => { wearLook(el, textLooks.get(uid)); paintFilter(el); });   // after its own style is set

  el.style.cssText = `
    position: fixed;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    background: transparent;
    color: var(--oscilla-text-colour, #000);
    font-size: 4em;
    padding: 0;
    max-width: ${wrap ? "70vw" : "none"};
    text-align: center;
    pointer-events: auto;
    cursor: pointer;
    opacity: 0;
    transition: opacity ${fadeMs}ms ease;
    white-space: ${wrap ? "pre-line" : "pre"};
    ${defaultFontCss()}
    ${style}
  `;
  wearWordLook(el, uid);
  if (trailSecs.has(uid)) wakeTrails();

  return el;
}

function positionOverlay(el, targetId, offsetX, offsetY, cueElement, uid) {
  if (targetId === "self" && cueElement) {
    const box = cueElement.getBoundingClientRect();
    // Position at center of the element
    el.style.position = "fixed";
    el.style.left = `${box.x + box.width / 2 + offsetX}px`;
    el.style.top = `${box.y + box.height / 2 + offsetY}px`;
    el.style.transform = "translate(-50%, -50%)";
    // Hide the cue element
    hideCueElement(cueElement, uid);
  } else if (targetId && targetId !== "center") {
    // Target is another element by ID
    const target = document.getElementById(targetId);
    if (target) {
      const box = target.getBoundingClientRect();
      el.style.position = "fixed";
      el.style.left = `${box.x + box.width / 2 + offsetX}px`;
      el.style.top = `${box.y + box.height / 2 + offsetY}px`;
      el.style.transform = "translate(-50%, -50%)";
      // Hide the target element (replacing it with text)
      hideCueElement(target, uid);
    }
  } else {
    // Center positioning (default) -- or where it was dragged / put
    const at = textPlaces.get(uid);
    el.style.left = `calc(${at ? at.x * 100 : 50}% + ${offsetX}px)`;
    el.style.top = `calc(${at ? at.y * 100 : 50}% + ${offsetY}px)`;
  }
}

/**
 * flash: -- the stage flashes while the word shows (screenFx stageFlash):
 * flash:white / "#f0f" its background that colour, flash:invert inverted.
 * The word stays a word on top; the console and the bars are not touched.
 * Released when the word goes, however it goes.
 */
function flashWith(el, flash) {
  // every word shown: <uid>.hit, so a sound can go with each one --
  // synth(..., trig:w.hit) (liveSignals pulse; <uid>.n counts them)
  pulse(el?.dataset?.uid, "text");
  if (!flash) return;
  const off = stageFlash(flash);
  el._flashOff = off;
  return off;
}
const flashOff = (el) => { el?._flashOff?.(); if (el) el._flashOff = null; };

function removeOverlaysByUid(uid) {
  document.querySelectorAll(`.cue-text-reveal[data-uid="${CSS.escape(uid)}"]`)
    .forEach(el => el.remove());
  const selector = `.cue-text-overlay[data-uid="${CSS.escape(uid)}"]`;
  document.querySelectorAll(selector).forEach(el => {
    flashOff(el);
    el.remove();
  });
  // Also remove scroll containers
  const scrollSelector = `.cue-text-scroll-container[data-uid="${CSS.escape(uid)}"]`;
  document.querySelectorAll(scrollSelector).forEach(el => {
    el.remove();
  });
  // Also remove ticker containers
  const tickerSelector = `.cue-text-ticker-container[data-uid="${CSS.escape(uid)}"]`;
  document.querySelectorAll(tickerSelector).forEach(el => {
    el.remove();
  });
  // Unregister from held overlays scroll tracking
  window._textCueHeldOverlays?.delete(uid);
  // Restore hidden element
  showCueElement(uid);
}

/* ------------------------------------------------------------------------
   5) Scroll Mode (Teleprompter)
-------------------------------------------------------------------------*/

function createScrollContainer(content, uid, style, speed, boundingBox = null, padding = 16, bg = "", wrap = true) {
  const container = document.createElement("div");
  container.className = "cue-text-scroll-container";
  container.dataset.uid = uid;

  // Use bounding box dimensions if provided, otherwise default to viewport-relative
  if (boundingBox) {
    container.style.cssText = `
      position: fixed;
      left: ${boundingBox.x - padding}px;
      top: ${boundingBox.y - padding}px;
      width: ${boundingBox.width + padding * 2}px;
      height: ${boundingBox.height + padding * 2}px;
      overflow: hidden;
      pointer-events: auto;
      cursor: pointer;
      user-select: none;
      -webkit-user-select: none;
      -webkit-tap-highlight-color: transparent;
      -webkit-touch-callout: none;
      background: ${bg || "transparent"};
    `;
  } else {
    container.style.cssText = `
      position: fixed;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      width: 80vw;
      height: 60vh;
      overflow: hidden;
      pointer-events: auto;
      cursor: pointer;
      user-select: none;
      -webkit-user-select: none;
      -webkit-tap-highlight-color: transparent;
      -webkit-touch-callout: none;
    `;
  }

  // Prevent selection on mousedown (Chrome fix)
  container.addEventListener("mousedown", (e) => e.preventDefault());
  container.addEventListener("selectstart", (e) => e.preventDefault());

  const inner = document.createElement("div");
  inner.className = "cue-text-scroll-inner";

  // Adjust padding based on container size
  const verticalPadding = boundingBox ? `${boundingBox.height}px` : "60vh";

  inner.style.cssText = `
    color: var(--oscilla-text-colour, #000);
    font-size: ${boundingBox ? "1.2em" : "2em"};
    line-height: 1.6;
    text-align: center;
    padding: ${verticalPadding} 0;
    margin: 0 auto;            /* a max-width: in style: -- a column down the middle */
    user-select: none;
    -webkit-user-select: none;
    pointer-events: none;
    ${defaultFontCss()}
    ${style}
  `;

  // Add content as paragraphs
  if (Array.isArray(content)) {
    content.forEach(item => {
      const p = document.createElement("p");
      p.textContent = typeof item === "object" ? item.text : item;
      p.style.margin = "0.5em 0";
      if (!wrap) p.style.whiteSpace = "pre";
      inner.appendChild(p);
    });
  } else {
    inner.textContent = content;
  }

  container.appendChild(inner);
  paintFilter(container);

  return { container, inner };
}

async function runScrollMode(params, items, token, cueElement) {
  const { uid, style, speed, targetId, offsetX, offsetY, pad, hold, bg } = params;
  const padding = Number(pad) || 16;

  let boundingBox = null;

  // If target:self, use the cue element's bounding box
  if (targetId === "self" && cueElement) {
    const box = cueElement.getBoundingClientRect();
    boundingBox = {
      x: box.x + offsetX,
      y: box.y + offsetY,
      width: box.width,
      height: box.height,
    };
    // Hide the cue element
    hideCueElement(cueElement, uid);
  } else if (targetId === "self" && !cueElement) {
    console.warn("[scroll] target:self but no cueElement provided!");
  }

  const { container, inner } = createScrollContainer(items, uid, style, speed, boundingBox, padding, bg, params.wrap !== false);

  // Position container for other target modes (not self, not center)
  if (targetId && targetId !== "center" && targetId !== "self") {
    positionOverlay(container, targetId, offsetX, offsetY, cueElement, uid);
  }

  document.body.appendChild(container);

  // Pause/resume state
  let paused = false;
  let currentY = 0;
  let remainingDistance = 0;
  let remainingMs = 0;
  let expectedEndTime = 0;
  let scrollHeight = 0; // Will be set after container is added to DOM

  // Manual double-click detection
  let lastClickTime = 0;
  let clickTimeout = null;
  const DOUBLE_CLICK_THRESHOLD = 300;

  // Helper to pause scrolling
  const pauseScroll = () => {
    if (paused) return;
    paused = true;
    container.style.cursor = "cell";
    const computed = getComputedStyle(inner);
    const matrix = new DOMMatrix(computed.transform);
    currentY = Math.abs(matrix.m42); // current Y translation
    remainingDistance = scrollHeight - currentY;
    inner.style.transition = "none";
    inner.style.transform = `translateY(-${currentY}px)`;
  };

  // Helper to resume scrolling
  const resumeScroll = () => {
    if (!paused) return;
    paused = false;
    container.style.cursor = "pointer";
    remainingMs = (remainingDistance / speed) * 1000;
    expectedEndTime = performance.now() + remainingMs;
    inner.style.transition = `transform ${remainingMs}ms linear`;
    inner.style.transform = `translateY(-${scrollHeight}px)`;
  };

  // Handle double-click confirmation
  const handleDoubleClick = async () => {
    // Cancel any pending single-click action
    if (clickTimeout) {
      clearTimeout(clickTimeout);
      clickTimeout = null;
    }

    // Pause while confirming
    const wasPaused = paused;
    pauseScroll();

    const confirmed = await showConfirmDialog("Close teleprompter?", "Close", "Cancel");
    if (confirmed) {
      token.cancel = true;
    } else if (!wasPaused) {
      resumeScroll();
    }
  };

  // Handle single-click toggle
  const handleSingleClick = () => {
    if (paused) {
      resumeScroll();
    } else {
      pauseScroll();
    }
  };

  // Click handler with manual double-click detection
  container.addEventListener("click", (e) => {
    e.stopPropagation();

    const now = performance.now();
    const timeSinceLastClick = now - lastClickTime;
    lastClickTime = now;

    // Double-click detected
    if (timeSinceLastClick < DOUBLE_CLICK_THRESHOLD) {
      if (clickTimeout) {
        clearTimeout(clickTimeout);
        clickTimeout = null;
      }
      handleDoubleClick();
      lastClickTime = 0; // Reset to prevent triple-click triggering another double
      return;
    }

    // Delay single-click to allow for double-click
    if (clickTimeout) {
      clearTimeout(clickTimeout);
    }
    clickTimeout = setTimeout(() => {
      clickTimeout = null;
      handleSingleClick();
    }, DOUBLE_CLICK_THRESHOLD);
  });

  // Fade in
  container.style.opacity = "0";
  container.style.transition = "opacity 500ms ease";
  container.style.cursor = "pointer";
  await new Promise(r => requestAnimationFrame(r));
  container.style.opacity = "1";

  // Calculate scroll distance and duration
  scrollHeight = inner.scrollHeight - container.clientHeight;
  remainingDistance = scrollHeight;

  // Start scrolling
  remainingMs = (remainingDistance / speed) * 1000;
  inner.style.transition = `transform ${remainingMs}ms linear`;
  await new Promise(r => requestAnimationFrame(r));
  inner.style.transform = `translateY(-${scrollHeight}px)`;

  // Wait for scroll to complete or cancel
  expectedEndTime = performance.now() + remainingMs;
  while (!token.cancel) {
    await rafWait(100, token);

    if (paused) {
      // While paused, push back the expected end time
      expectedEndTime = performance.now() + remainingMs;
    } else {
      // Check if we've reached the end
      if (performance.now() >= expectedEndTime) break;
    }
  }

  // If hold:true, keep the scroll container visible at final position
  if (hold && !token.cancel) {
    // Register for scroll tracking
    registerHeldOverlay(uid, container, cueElement, targetId, offsetX, offsetY, "scroll");
    return; // Exit without removing - container stays on screen
  }

  // Fade out and remove
  container.style.transition = "opacity 500ms ease";
  container.style.opacity = "0";
  await rafWait(500, token);

  container.remove();
  // Restore hidden cue element if target:self was used
  showCueElement(uid);
}

/* ------------------------------------------------------------------------
   5b) Ticker Mode (News Crawl)
-------------------------------------------------------------------------*/

function createTickerContainer(content, uid, style, boundingBox = null, padding = 16, bg = "") {
  const container = document.createElement("div");
  container.className = "cue-text-ticker-container";
  container.dataset.uid = uid;

  // Use bounding box dimensions if provided, otherwise default bar at bottom
  if (boundingBox) {
    container.style.cssText = `
      position: fixed;
      left: ${boundingBox.x}px;
      top: ${boundingBox.y}px;
      width: ${boundingBox.width}px;
      height: ${boundingBox.height}px;
      overflow: hidden;
      pointer-events: auto;
      cursor: pointer;
      user-select: none;
      -webkit-user-select: none;
      -webkit-tap-highlight-color: transparent;
      background: ${bg || "transparent"};
      display: flex;
      align-items: center;
    `;
  } else {
    container.style.cssText = `
      position: fixed;
      left: 0;
      bottom: 10%;
      width: 100vw;
      height: auto;
      overflow: hidden;
      pointer-events: auto;
      cursor: pointer;
      user-select: none;
      -webkit-user-select: none;
      -webkit-tap-highlight-color: transparent;
      background: ${bg || "transparent"};
      padding: ${padding}px 0;
      display: flex;
      align-items: center;
    `;
  }

  // Prevent selection on mousedown (Chrome fix)
  container.addEventListener("mousedown", (e) => e.preventDefault());
  container.addEventListener("selectstart", (e) => e.preventDefault());

  const inner = document.createElement("div");
  inner.className = "cue-text-ticker-inner";

  inner.style.cssText = `
    color: var(--oscilla-text-colour, #000);
    font-size: ${boundingBox ? "1.2em" : "2em"};
    white-space: nowrap;
    user-select: none;
    -webkit-user-select: none;
    pointer-events: none;
    display: inline-block;
    padding-left: 100%;
    ${defaultFontCss()}
    ${style}
  `;

  // Build content string
  let textContent = "";
  if (Array.isArray(content)) {
    textContent = content.map(item => typeof item === "object" ? item.text : item).join("  •  ");
  } else {
    textContent = String(content);
  }

  inner.textContent = textContent;
  container.appendChild(inner);
  paintFilter(container);

  return { container, inner };
}

async function runTickerMode(params, items, token, cueElement) {
  const { uid, style, speed, targetId, offsetX, offsetY, pad, direction, loop: loopParam, hold } = params;
  const padding = Number(pad) || 16;

  let boundingBox = null;

  // If target:self, use the cue element's bounding box
  if (targetId === "self" && cueElement) {
    const box = cueElement.getBoundingClientRect();
    boundingBox = {
      x: box.x + offsetX,
      y: box.y + offsetY,
      width: box.width,
      height: box.height,
    };
    hideCueElement(cueElement, uid);
  }

  // Build content string from items
  const textContent = items.map(item => item.text).join("  •  ");

  const { container, inner } = createTickerContainer(textContent, uid, style, boundingBox, padding, params.bg);

  document.body.appendChild(container);

  const loopCount = loopCountOf(loopParam);

  // Click to dismiss
  container.addEventListener("click", (e) => {
    e.stopPropagation();
    token.cancel = true;
  });

  // Fade in
  container.style.opacity = "0";
  container.style.transition = "opacity 500ms ease";
  await new Promise(r => requestAnimationFrame(r));
  container.style.opacity = "1";

  // Wait for fade and layout
  await rafWait(100, token);

  // Calculate scroll distance
  const textWidth = inner.scrollWidth;
  const containerWidth = container.clientWidth;
  const totalDistance = textWidth + containerWidth; // Text needs to scroll its full width plus container width

  // Speed is in px/sec
  const durationMs = (totalDistance / speed) * 1000;

  // Direction: left means text moves left (standard news ticker)
  const isLeftward = direction !== "right";

  // Run loops
  for (let i = 0; i < loopCount && !token.cancel; i++) {
    // Reset position
    if (isLeftward) {
      inner.style.transition = "none";
      inner.style.transform = "translateX(0)";
      inner.style.paddingLeft = "100%";
    } else {
      inner.style.transition = "none";
      inner.style.paddingLeft = "0";
      inner.style.transform = `translateX(-${textWidth}px)`;
    }

    await new Promise(r => requestAnimationFrame(r));

    // Animate
    inner.style.transition = `transform ${durationMs}ms linear`;

    if (isLeftward) {
      inner.style.transform = `translateX(-${totalDistance}px)`;
    } else {
      inner.style.transform = `translateX(${containerWidth}px)`;
    }

    // Wait for animation to complete
    await rafWait(durationMs, token);
  }

  // If hold:true, keep the ticker container visible
  if (hold && !token.cancel) {
    // Register for scroll tracking
    registerHeldOverlay(uid, container, cueElement, targetId, offsetX, offsetY, "ticker");
    return; // Exit without removing - container stays on screen
  }

  // Fade out and remove
  container.style.transition = "opacity 500ms ease";
  container.style.opacity = "0";
  await rafWait(500, token);

  container.remove();
  showCueElement(uid);
}

/* ------------------------------------------------------------------------
   6) Pick/Cycle/Shuffle Modes
-------------------------------------------------------------------------*/

async function runPickMode(params, items, token, cueElement, selectedIndex = null) {
  const { uid, style, fadeMs, dur, targetId, offsetX, offsetY, hold } = params;

  // Pick item: use provided index (from network sync) or pick randomly
  const index = selectedIndex !== null ? selectedIndex : Math.floor(Math.random() * items.length);
  const item = items[index];
  const text = item.text;
  const itemDur = item.dur ?? dur;
  if (item.words?.length > 1) return runWordGroup(params, item.words, itemDur, token, cueElement);

  const overlay = createTextOverlay(text, uid, style, fadeMs, params.wrap !== false);
  positionOverlay(overlay, targetId, offsetX, offsetY, cueElement, uid);

  overlay.addEventListener("click", () => {
    token.cancel = true;
  });

  document.body.appendChild(overlay);
  placeAt(params, overlay);
  startArrival(params, overlay);

  // Fade in
  await new Promise(r => requestAnimationFrame(r));
  overlay.style.opacity = "1";
  arrive(overlay);
  flashWith(overlay, params.flash);
  const t0 = performance.now(), said = sayWith(params, text);

  // Wait for duration -- and for the voice, if it speaks
  await rafWait(itemDur * 1000, token);
  await untilSaid(said, t0, token);
  flashOff(overlay);

  // If hold:true, keep the overlay visible (don't fade out or remove)
  if (hold && !token.cancel) {
    // Register for scroll tracking
    registerHeldOverlay(uid, overlay, cueElement, targetId, offsetX, offsetY, "pick");
    return; // Exit without removing - overlay stays on screen
  }

  // Fade out
  if (!token.cancel) {
    overlay.style.opacity = "0";
    await rafWait(fadeMs, token);
  }

  overlay.remove();
  // Restore hidden cue element if target:self was used
  showCueElement(uid);
}

/**
 * A list in the list, pick:["WTF", ["BRO", "WHAT"]]: its words one after
 * another in the time of one -- two in halves, three in triplets -- of a
 * beat (or of dur: when that is longer; span: sets it), so they stay on the
 * beat the single words land on. Each shows for its share of dur:.
 */
async function runWordGroup(params, words, itemDur, token, cueElement) {
  const { uid, style, fadeMs, targetId, offsetX, offsetY, hold } = params;
  const n = words.length;
  const span = params.span ?? Math.max(itemDur, secPerBeat());
  const slot = span / n;
  const show = Math.min(itemDur / n, slot);
  const fade = Math.min(fadeMs, show * 500);
  const t0 = performance.now();
  const going = [];
  for (let i = 0; i < n && !token.cancel; i++) {
    const wait = t0 + i * slot * 1000 - performance.now();
    if (wait > 0) await rafWait(wait, token);
    if (token.cancel) break;
    const overlay = createTextOverlay(words[i], uid, style, fade, params.wrap !== false);
    positionOverlay(overlay, targetId, offsetX, offsetY, cueElement, i === 0 ? uid : null);
    overlay.addEventListener("click", () => { token.cancel = true; });
    document.body.appendChild(overlay);
    placeAt(params, overlay);
    startArrival(params, overlay);
    await new Promise(r => requestAnimationFrame(r));
    overlay.style.opacity = "1";
    arrive(overlay);
    flashWith(overlay, params.flash);
    const t1 = performance.now(), said = sayWith(params, words[i]);
    if (hold && i === n - 1) { registerHeldOverlay(uid, overlay, cueElement, targetId, offsetX, offsetY, "pick"); break; }
    const last = i === n - 1;
    going.push((async () => {
      await rafWait(show * 1000, token);
      // a word's voice is cut by the next word's: only the last is waited for
      if (last) await untilSaid(said, t1, token);
      flashOff(overlay);
      if (!token.cancel) { overlay.style.opacity = "0"; await rafWait(fade, token); }
      overlay.remove();
    })());
  }
  await Promise.all(going);
  if (!(hold && !token.cancel)) showCueElement(uid);
}

/**
 * say: -- the word spoken as it shows (say.js). Returns a promise of how
 * long the voice takes, or null when the text does not speak.
 */
function sayWith(params, text) {
  if (!params.say) return null;
  return import("./say.js").then(m => m.sayWord(text, params)).catch(() => 0);
}

/** Wait, from `t0`, until the voice that started then is done. */
async function untilSaid(said, t0, token) {
  if (!said) return;
  const dur = await said;
  const left = t0 + dur * 1000 - performance.now();
  if (left > 0 && !token?.cancel) await rafWait(left, token);
}

/**
 * A speaking text's words, made ahead so each starts on its beat: a list
 * of words all of them, a long cycle (a file of sentences) its first few
 * from where it starts -- each of the rest is made while the one before
 * is shown, in a fraction of the time it takes to say.
 */
const AHEAD_ALL = 40, AHEAD_FIRST = 4;
function prepareSaid(params, items) {
  if (!params.say || !items?.length) return;
  let list = items;
  if (items.length > AHEAD_ALL) {
    const k = params.mode === "cycle" && params.from ? (params.from - 1) % items.length : 0;
    list = params.mode === "cycle" ? [...items.slice(k), ...items.slice(0, k)].slice(0, AHEAD_FIRST) : [];
  }
  const words = list.flatMap(it => it.words?.length ? it.words : [it.text]);
  const args = params.say === true ? {} : { voice: params.say };
  if (params.sayRate != null) args.rate = params.sayRate;
  import("./say.js").then(m => m.prepareSpeech(params.uid, words, args)).catch(() => {});
}

/** When each text stepped by a signal last stepped: uid -> performance.now() (its fade fits between steps). */
const lastStepAt = new Map();

// Where each stepped cycle / shuffle is: uid -> { i, n, order }
const stepPlaces = new Map();

/** A cycle's from: -- where round it starts (0 the first item). */
const fromAt = (params, n) => params.mode === "cycle" && params.from ? (params.from - 1) % n : 0;

// =================================================================
//  WHERE A CYCLE HAS READ TO
//  A console text run again on the same content -- another size:, style:,
//  at: ... -- reads on from the item after the one it showed last: tuning a
//  line does not start the file over. stop:1, other content (or from:), or
//  the end of its last pass: from the top again. A score's text (a cue on
//  the page) starts from the top each time, as before.
// =================================================================

const readPlaces = new Map();          // uid -> { key, next }
// where viz() put a text, its looks and its words' rolls, by uid: the
// project's (a text of the same uid in the next one starts plain)
onReset("project", function forgetTextPlaces() {
  for (const m of [textPlaces, textLooks, wordLooks, readPlaces]) m.clear();
});
const readKey = p => JSON.stringify([p.content, p.unit, p.range, p.col, p.format, p.from]);

/** Where this run of a cycle starts (0 the first item): on from where it was, else from:. */
function readFrom(params, n) {
  const kept = params.live ? readPlaces.get(params.uid) : null;
  if (kept && kept.key === readKey(params)) return kept.next % n;
  readPlaces.delete(params.uid);
  return fromAt(params, n);
}

/** Item i (0 the first) of n is showing: the next run starts after it. */
function readTo(params, i, n) {
  if (params.live && i >= 0) readPlaces.set(params.uid, { key: readKey(params), next: (i + 1) % n });
}

/** The next item of a stepped cycle (in order) or shuffle (a fresh order each time round). */
function nextStep(uid, n, shuffle) {
  let at = stepPlaces.get(uid);
  if (!at || at.n !== n || at.shuffle !== shuffle) at = { i: -1, n, shuffle, order: null };
  at.i++;
  if (at.i >= n || !at.order) {
    if (at.i >= n) at.i = 0;
    at.order = shuffle ? generateShuffledSequence(n, 1) : null;
  }
  stepPlaces.set(uid, at);
  return at.order ? at.order[at.i] : at.i;
}

// =================================================================
//  STAY: EVERY ITEM STAYS WHERE IT LANDED
//  stay:1 -- each item comes in as any other does (its fade, its flash:),
//  shows for dur:, then is painted where it stands onto one canvas over the
//  page, and its overlay goes: a .csv of 11,000 names piles up on one
//  canvas, not in 11,000 elements. With at:rand each lands somewhere new.
//  Run again (another style, another size) they go on piling up;
//  text(uid:n, stop:1) clears them.
// =================================================================

const stayLayers = new Map();   // uid -> { canvas, marks }
// over the stage, under the console (10100, 38000 performing), the bars and
// the menus: the screen fills up behind the code, not over it
const STAY_Z = 9990;
const stayGens = new Map();     // uid -> bumped on each clear: an item still coming in is not painted

function stayLayer(uid) {
  let layer = stayLayers.get(uid);
  if (layer?.canvas.isConnected) return layer;
  const canvas = document.createElement("canvas");
  canvas.className = "cue-text-stay";
  canvas.dataset.uid = uid;
  canvas.style.cssText = `position:fixed; left:0; top:0; width:100vw; height:100vh; pointer-events:none; z-index:${STAY_Z};`;
  paintFilter(canvas);
  document.body.appendChild(canvas);
  layer = { canvas, marks: [] };
  stayLayers.set(uid, layer);
  fitStay(layer);
  return layer;
}

/** The canvas the window's size (sharp on a dense screen); resized, all of it painted again. */
function fitStay(layer) {
  const dpr = window.devicePixelRatio || 1;
  const w = Math.round(innerWidth * dpr), h = Math.round(innerHeight * dpr);
  const c = layer.canvas;
  if (c.width === w && c.height === h) return false;
  c.width = w; c.height = h;
  const ctx = c.getContext("2d");
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  for (const m of layer.marks) paintMark(ctx, m);
  return true;
}
window.addEventListener?.("resize", () => { for (const layer of stayLayers.values()) fitStay(layer); });

function paintMark(ctx, m) {
  const dpr = window.devicePixelRatio || 1;
  ctx.save();
  ctx.font = m.font;
  ctx.fillStyle = m.color;
  ctx.globalAlpha = m.alpha;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  if (m.spacing && "letterSpacing" in ctx) ctx.letterSpacing = m.spacing;
  if (m.shadow) {
    // a shadow is in the canvas's own pixels, not scaled with the drawing
    ctx.shadowColor = m.shadow.color;
    ctx.shadowOffsetX = m.shadow.x * dpr;
    ctx.shadowOffsetY = m.shadow.y * dpr;
    ctx.shadowBlur = m.shadow.blur * dpr;
  }
  for (const l of m.lines) ctx.fillText(l.text, l.x * innerWidth + (m.ox || 0), l.y * innerHeight + (m.oy || 0));
  ctx.restore();
}

/**
 * The lines an overlay is shown in, as the page broke them (a long excerpt
 * wrapped at the box's width): each its text and its middle, 0..1 of the
 * window -- painted there, it lands on itself.
 */
function overlayLines(el) {
  // the lines' layout, measured a glyph at a time, kept by the text and the
  // box's size: each frame after only reads where the box is now
  const r = el.getBoundingClientRect();
  const key = `${el.textContent}|${Math.round(r.width)}x${Math.round(r.height)}`;
  if (el._trailLines?.key !== key) {
    el._trailLines = { key, rel: measureLines(el, r).map(l => ({ text: l.text, dx: l.x * innerWidth - r.left, dy: l.y * innerHeight - r.top })) };
  }
  return el._trailLines.rel.map(l => ({ text: l.text, x: (r.left + l.dx) / innerWidth, y: (r.top + l.dy) / innerHeight }));
}

function measureLines(el, r) {
  const node = el.firstChild;
  const whole = [{ text: el.textContent, x: (r.left + r.width / 2) / innerWidth, y: (r.top + r.height / 2) / innerHeight }];
  if (!node || node.nodeType !== 3 || el.childNodes.length !== 1) return whole;
  const s = node.data, range = document.createRange();
  const lines = [];
  let cur = null;
  const done = () => {
    if (!cur) return;
    const text = cur.text.trim();
    if (text && Number.isFinite(cur.left)) {
      lines.push({ text, x: (cur.left + cur.right) / 2 / innerWidth, y: (cur.top + cur.bottom) / 2 / innerHeight });
    }
    cur = null;
  };
  for (let i = 0; i < s.length; i++) {
    if (s[i] === "\n") { done(); continue; }
    range.setStart(node, i);
    range.setEnd(node, i + 1);
    const box = range.getClientRects()[0];
    // a space where the line breaks has no box of its own: it ends the line
    if (!box || !box.width) { if (cur) cur.text += s[i]; continue; }
    if (cur && box.top >= cur.bottom - 1) done();
    if (!cur) cur = { text: "", left: Infinity, right: -Infinity, top: box.top, bottom: box.bottom };
    cur.text += s[i];
    cur.left = Math.min(cur.left, box.left);
    cur.right = Math.max(cur.right, box.right);
    cur.top = Math.min(cur.top, box.top);
    cur.bottom = Math.max(cur.bottom, box.bottom);
  }
  done();
  return lines.length ? lines : whole;
}

/** The first of a computed text-shadow: "rgba(0, 0, 0, 0.7) 0px 0px 10px". */
function firstShadow(css) {
  const m = /^\s*(rgba?\([^)]*\)|#[\da-f]+|[a-z]+)\s+(-?[\d.]+)px\s+(-?[\d.]+)px(?:\s+([\d.]+)px)?/i.exec(css || "");
  if (!m || /^none$/i.test(m[1])) return null;
  return { color: m[1], x: Number(m[2]), y: Number(m[3]), blur: Number(m[4] || 0) };
}

/**
 * Marks that change: each frame the canvas painted again -- one past its
 * keep: fading over its fade and then dropped; one painted inside a shape
 * (in:) moved with the shape. Runs while there are any.
 */
let stayRaf = 0;
const moving = m => m.keep || m.anchor;
function repaintStays(now) {
  stayRaf = 0;
  let more = false;
  const centres = new Map();
  for (const layer of stayLayers.values()) {
    if (!layer.marks.some(moving)) continue;
    layer.marks = layer.marks.filter(m => !m.keep || now - m.t < m.keep + m.fade);
    const ctx = layer.canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, layer.canvas.width, layer.canvas.height);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    for (const m of layer.marks) {
      const past = m.keep ? now - m.t - m.keep : 0;
      const c = m.anchor && shapeCentre(m.anchor.shape, m.anchor.body, centres);
      const moved = c ? { ox: c[0] - m.anchor.cx, oy: c[1] - m.anchor.cy } : null;
      paintMark(ctx, past > 0 || moved ? { ...m, ...moved, alpha: m.alpha * (past > 0 ? 1 - past / (m.fade || 1) : 1) } : m);
    }
    if (layer.marks.some(moving)) more = true;
  }
  if (more) stayRaf = requestAnimationFrame(repaintStays);
}

/** Paint an overlay onto its uid's stay canvas, as it looks now, where it stands (keep: ms, then gone over fade: ms). */
function bakeOverlay(uid, el, keep = null, fade = 0) {
  const layer = stayLayer(uid);
  fitStay(layer);
  el.style.scale = "";            // measured at its own size, however far it has come
  const cs = getComputedStyle(el);
  const filterAlpha = /opacity\(([\d.]+)\)/.exec(cs.filter || "")?.[1];
  const mark = {
    lines: overlayLines(el),
    font: `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`,
    color: cs.color,
    alpha: filterAlpha != null ? Number(filterAlpha) : 1,
    spacing: cs.letterSpacing !== "normal" ? cs.letterSpacing : null,
    shadow: firstShadow(cs.textShadow),
  };
  if (keep) Object.assign(mark, { t: performance.now(), keep, fade: Math.max(fade, 50) });
  // inside a shape (in:): painted where the shape is now, and moved with it after
  if (el._in) {
    const c = shapeCentre(el._in.shape, el._in.body, new Map());
    if (c) mark.anchor = { shape: el._in.shape, body: el._in.body, cx: c[0], cy: c[1] };
  }
  if (moving(mark) && !stayRaf) stayRaf = requestAnimationFrame(repaintStays);
  layer.marks.push(mark);
  paintMark(layer.canvas.getContext("2d"), mark);
  // blend: -- the canvas mixes with what is under it as the words did
  layer.canvas.style.mixBlendMode = cs.mixBlendMode || "";
}

function clearStay(uid) {
  stayGens.set(uid, (stayGens.get(uid) || 0) + 1);
  flowPlaces.delete(uid);
  insidePlaces.delete(uid);
  const layer = stayLayers.get(uid);
  layer?.canvas.remove();
  stayLayers.delete(uid);
  clearTrail(uid);
}

// =================================================================
//  TRAIL: viz(uid, trail:2)
//  Every word of the text showing is painted each frame onto a canvas of its
//  own, which fades over the trail's time (a shape's trail fade, scopeViews
//  fadeTrail): a word that moves (in:spot on the mouse) smears behind it, a
//  word that goes (the next one in) leaves an after-image. Under the words,
//  with the text's viz() effects; 0 takes it away. Painted as the word looks
//  now -- its font, colour, opacity -- not its arrival's scale.
// =================================================================

const trailSecs = new Map();     // uid -> seconds a trail takes to fade
const trailLayers = new Map();   // uid -> { canvas, seen } (fadeTrail keeps its tick here too)
let trailRaf = 0;

/** viz(uid, trail:secs) on a text: 0 takes it away. */
export function setTextTrail(uid, secs) {
  if (secs > 0) trailSecs.set(uid, secs);
  else { trailSecs.delete(uid); clearTrail(uid); }
  wakeTrails();
}

function clearTrail(uid) {
  trailLayers.get(uid)?.canvas.remove();
  trailLayers.delete(uid);
}

function wakeTrails() {
  if (!trailRaf && trailSecs.size) trailRaf = requestAnimationFrame(trailFrame);
}

function trailLayer(uid) {
  let t = trailLayers.get(uid);
  if (t?.canvas.isConnected) return t;
  const canvas = document.createElement("canvas");
  canvas.className = "cue-text-trail";
  canvas.dataset.uid = uid;
  canvas.style.cssText = "position:fixed; left:0; top:0; width:100vw; height:100vh; pointer-events:none;";
  paintFilter(canvas);
  document.body.appendChild(canvas);
  t = { canvas, seen: 0 };
  trailLayers.set(uid, t);
  return t;
}

function trailFrame(now) {
  trailRaf = 0;
  const dpr = window.devicePixelRatio || 1;
  const w = Math.round(innerWidth * dpr), h = Math.round(innerHeight * dpr);
  for (const [uid, secs] of trailSecs) {
    const words = [...document.querySelectorAll(`.cue-text-overlay[data-uid="${CSS.escape(uid)}"]`)];
    let t = trailLayers.get(uid);
    if (!words.length) {
      // nothing showing: faded out by now, the canvas goes until a word comes
      if (!t) continue;
      if (now - t.seen > (secs + 0.5) * 1000) { clearTrail(uid); continue; }
    } else {
      t = trailLayer(uid);
      t.seen = now;
    }
    const c = t.canvas;
    if (c.width !== w || c.height !== h) { c.width = w; c.height = h; }
    const g = c.getContext("2d");
    g.setTransform(1, 0, 0, 1, 0, 0);
    fadeTrail(t, c, secs);
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    for (const el of words) {
      const cs = getComputedStyle(el);
      const filterAlpha = /opacity\(([\d.]+)\)/.exec(el.style.getPropertyValue("--oscilla-fx") || "")?.[1];
      const alpha = Number(cs.opacity) * (filterAlpha != null ? Number(filterAlpha) : 1);
      if (!(alpha > 0.01) || !el.textContent.trim()) continue;
      paintMark(g, {
        lines: overlayLines(el),
        font: `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`,
        color: cs.color,
        alpha,
        spacing: cs.letterSpacing !== "normal" ? cs.letterSpacing : null,
        shadow: firstShadow(cs.textShadow),
      });
      c.style.mixBlendMode = cs.mixBlendMode || "";
    }
  }
  // on while a trail is still fading; else asleep until a word comes (createTextOverlay)
  if ([...trailSecs.keys()].some(u => trailLayers.has(u))) wakeTrails();
}

/**
 * at:rand -- the overlay somewhere new on the screen, all of it inside the
 * window (a name longer than the window: across the middle).
 */
function scatter(el) {
  const r = el.getBoundingClientRect();
  const place = (size, room) => (size >= room ? room / 2 : size / 2 + Math.random() * (room - size));
  el.style.left = `${place(r.width, innerWidth)}px`;
  el.style.top = `${place(r.height, innerHeight)}px`;
}

/**
 * at:flow -- the overlay under the one before it, from the top left, as a
 * page reads; at the bottom, the next column (as wide as the widest in this
 * one); past the right edge, back to the top left, over what is there --
 * with stay:1 the page goes on darkening. Where the next one goes is kept
 * for each uid, run again or not; text(uid, stop:1) starts it over.
 */
const flowPlaces = new Map();   // uid -> { x, y, colW }
const FLOW_MARGIN = 24;

function flowPlace(uid, el) {
  el.style.transform = "none";
  el.style.left = `${FLOW_MARGIN}px`;
  el.style.top = "0px";
  const r = el.getBoundingClientRect();
  const gap = (parseFloat(getComputedStyle(el).fontSize) || 16) * 0.6;
  let at = flowPlaces.get(uid) || { x: FLOW_MARGIN, y: FLOW_MARGIN, colW: 0 };
  if (at.y > FLOW_MARGIN && at.y + r.height > innerHeight - FLOW_MARGIN) {
    at = { x: at.x + at.colW + gap * 2, y: FLOW_MARGIN, colW: 0 };
  }
  if (at.x > FLOW_MARGIN && at.x + r.width > innerWidth - FLOW_MARGIN) {
    at = { x: FLOW_MARGIN, y: FLOW_MARGIN, colW: 0 };
  }
  el.style.left = `${at.x}px`;
  el.style.top = `${at.y}px`;
  flowPlaces.set(uid, { x: at.x, y: at.y + r.height + gap, colW: Math.max(at.colW, r.width) });
}

/**
 * at:rows -- the overlay a row down from the last one, at a random place
 * across (all of it on the screen); past the bottom, back to the top. One
 * row for every text with at:rows, so four agents speaking take turns down
 * the screen and never land on each other (at:rand can). still() and a
 * hush of the texts start it over at the top.
 */
let rowY = FLOW_MARGIN;

function rowPlace(el) {
  el.style.transform = "none";
  el.style.left = "0px";
  el.style.top = "0px";
  const r = el.getBoundingClientRect();
  const gap = (parseFloat(getComputedStyle(el).fontSize) || 16) * 0.6;
  if (rowY > FLOW_MARGIN && rowY + r.height > innerHeight - FLOW_MARGIN) rowY = FLOW_MARGIN;
  const room = innerWidth - 2 * FLOW_MARGIN - r.width;
  el.style.left = `${room > 0 ? FLOW_MARGIN + Math.random() * room : Math.max(0, (innerWidth - r.width) / 2)}px`;
  el.style.top = `${rowY}px`;
  rowY += r.height + gap;
}

/**
 * Inside a shape (a cluster: one of its bodies), read where it is on the
 * screen now, so the words follow a shape that moves; the words themselves
 * are not cut at its edge. No such shape: anywhere, as at:rand.
 *   rand  the overlay's middle at a random point inside   (at:spot, in:spot)
 *   mid   at the middle of the shape's box               (in:spot, at:mid)
 *   rows  each a row below the last, across the middle of the shape there;
 *         past the bottom, the top row again              (in:spot, at:rows)
 *   flow  side by side along a row as wide as the shape is there, then
 *         the next row, as a page is written              (in:spot, at:flow)
 * Where rows and flow are up to -- the row, the place along it -- is kept for
 * each uid, so a shape that moves carries on where it was; text(uid, stop:1)
 * starts it over.
 */
const insidePlaces = new Map();  // uid -> { row, x }  (x: px from the row's left edge)

/** The middle of a shape's body on the screen now ([x, y]), each looked up once a frame (cache). */
function shapeCentre(shape, body, cache) {
  const key = `${shape}#${body}`;
  if (!cache.has(key)) {
    const box = outlineBox(outlinesOf(shape)[body]);
    cache.set(key, box ? [(box.x0 + box.x1) / 2, (box.y0 + box.y1) / 2] : null);
  }
  return cache.get(key);
}

// in: -- an overlay inside a shape goes where the shape goes while it shows
const carried = new Set();
let carryRaf = 0;
function carryFrame() {
  carryRaf = 0;
  const centres = new Map();
  for (const el of carried) {
    if (!el.isConnected) { carried.delete(el); continue; }
    const c = shapeCentre(el._in.shape, el._in.body, centres);
    if (c) { el.style.left = `${c[0] + el._in.dx}px`; el.style.top = `${c[1] + el._in.dy}px`; }
  }
  if (carried.size) carryRaf = requestAnimationFrame(carryFrame);
}

function insidePlace(el, shape, mode, uid, carry = false) {
  const bodies = outlinesOf(shape);
  const body = Math.floor(Math.random() * bodies.length);
  const pts = bodies[body];
  const box = outlineBox(pts);
  if (!box) { scatter(el); return; }
  let p;
  if (mode === "mid") p = [(box.x0 + box.x1) / 2, (box.y0 + box.y1) / 2];
  else if (mode === "rows" || mode === "flow") {
    const r = el.getBoundingClientRect();
    const fs = parseFloat(getComputedStyle(el).fontSize) || 16;
    const rowH = r.height + fs * 0.15;
    const n = Math.max(1, Math.floor((box.y1 - box.y0) / rowH));
    const top = (box.y0 + box.y1) / 2 - (n * rowH) / 2;
    const at = insidePlaces.get(uid) || { row: -1, x: Infinity };
    const yOf = row => top + rowH * (row + 0.5);
    const chord = row => chordAt(pts, yOf(row)) || [box.x0, box.x1];
    if (mode === "rows") {
      at.row = (at.row + 1) % n;
      const [a, b] = chord(at.row);
      p = [(a + b) / 2, yOf(at.row)];
    } else {
      const gap = fs * 0.35;
      let [a, b] = chord(Math.max(0, at.row));
      // the next row when this one is full (a word wider than a row: a row of its own)
      if (at.row < 0 || a + at.x + r.width > b && at.x > 0) {
        at.row = (at.row + 1) % n;
        at.x = 0;
        [a, b] = chord(at.row);
      }
      p = [a + at.x + r.width / 2, yOf(at.row)];
      at.x += r.width + gap;
    }
    insidePlaces.set(uid, at);
  } else p = randomPointIn(pts);
  el.style.left = `${p[0]}px`;
  el.style.top = `${p[1]}px`;
  if (carry) {
    const cx = (box.x0 + box.x1) / 2, cy = (box.y0 + box.y1) / 2;
    el._in = { shape, body, dx: p[0] - cx, dy: p[1] - cy };
    carried.add(el);
    if (!carryRaf) carryRaf = requestAnimationFrame(carryFrame);
  }
}

/** at:rand / at:flow / at:rows / at:spot / in:spot on an overlay just put on the page. */
function placeAt(params, el) {
  if (params.inside) insidePlace(el, params.inside, params.insideMode, params.uid, params.carry);
  else if (params.scatter) scatter(el);
  else if (params.flow) flowPlace(params.uid, el);
  else if (params.rows) rowPlace(el);
}

/** One item put up to stay: in like any other, its dur: up, then painted down. */
async function stayOne(params, item, cueElement) {
  const { uid, style, fadeMs, dur, targetId, offsetX, offsetY } = params;
  const gen = stayGens.get(uid) || 0;
  const overlay = createTextOverlay(item.text, uid, style, fadeMs, params.wrap !== false);
  positionOverlay(overlay, targetId, offsetX, offsetY, cueElement, null);
  overlay.style.zIndex = STAY_Z;
  document.body.appendChild(overlay);
  placeAt(params, overlay);
  startArrival(params, overlay);
  await new Promise(r => requestAnimationFrame(r));
  if (!overlay.isConnected) return;          // stopped before it showed (still(), stop:1)
  overlay.style.opacity = "1";
  arrive(overlay);
  flashWith(overlay, params.flash);
  const t0 = performance.now(), said = sayWith(params, item.text);
  const upMs = (item.dur ?? dur) * 1000;
  await rafWait(upMs);
  await untilSaid(said, t0);
  flashOff(overlay);
  // painted only once it is all the way in
  if (fadeMs > upMs) await rafWait(fadeMs - upMs);
  if (overlay.isConnected && (stayGens.get(uid) || 0) === gen) bakeOverlay(uid, overlay, params.keepMs, fadeMs);
  overlay.remove();
}

/** A cycle / shuffle with stay:1 and no trig: one item each dur:, every one staying. */
async function runStayMode(params, items, token, cueElement) {
  const loopCount = loopCountOf(params.loop);
  const start = params.mode === "shuffle" ? 0 : readFrom(params, items.length);
  for (let pass = 0; pass < loopCount && !token.cancel; pass++) {
    const order = params.mode === "shuffle" ? generateShuffledSequence(items.length, 1) : null;
    for (let i = 0; i < items.length && !token.cancel; i++) {
      const at = order ? order[i] : (i + start) % items.length;
      const shown = stayOne(params, items[at], cueElement);
      noteLine(params.uid, at + 1);
      if (!order) readTo(params, at, items.length);
      // a speaking one waits for its voice (and is painted): the next would cut it off
      await (params.say ? shown : rafWait((items[at].dur ?? params.dur) * 1000, token));
    }
  }
}

/**
 * Generate a long shuffled index sequence for sync mode
 * @param {number} itemCount - Number of items
 * @param {number} loopCount - Number of loops (Infinity for long sequence)
 * @returns {number[]} Array of shuffled indices
 */
function generateShuffledSequence(itemCount, loopCount) {
  // For infinite or long loops, generate ~100 passes worth
  const passes = loopCount === Infinity ? 100 : loopCount;
  const sequence = [];

  for (let p = 0; p < passes; p++) {
    // indices 0..itemCount-1, shuffled
    sequence.push(...shuffleItems(Array.from({ length: itemCount }, (_, i) => i)));
  }

  return sequence;
}

async function runCycleMode(params, items, token, cueElement, shuffle = false, shuffledSequence = null) {
  const { uid, style, fadeMs, dur, targetId, offsetX, offsetY, hold } = params;
  // gap:<beats> (seconds by now) — each line fades out, the screen stays empty this long, then the
  // next fades in (instead of one crossfading into the next)
  const gap = Math.max(0, Number(params.gap) || 0);

  const loopCount = loopCountOf(params.loop);

  let currentOverlay = null;
  let sequenceIndex = 0;
  let elementHidden = false;
  // on from where a run before it was (readFrom), or from:
  const k = shuffle ? 0 : readFrom(params, items.length);

  for (let pass = 0; pass < loopCount && !token.cancel; pass++) {
    // Use pre-generated sequence if provided (for sync), otherwise shuffle locally
    let orderedItems;
    if (shuffledSequence && shuffle) {
      // Get next batch of indices from sequence
      orderedItems = [];
      for (let i = 0; i < items.length && sequenceIndex < shuffledSequence.length; i++) {
        orderedItems.push(items[shuffledSequence[sequenceIndex++]]);
      }
      // If sequence exhausted, wrap around
      if (orderedItems.length < items.length) {
        sequenceIndex = 0;
        for (let i = orderedItems.length; i < items.length; i++) {
          orderedItems.push(items[shuffledSequence[sequenceIndex++]]);
        }
      }
    } else {
      // round from the kth item, the rest after
      orderedItems = shuffle ? shuffleItems(items) : k ? [...items.slice(k), ...items.slice(0, k)] : items;
    }
    // a list in the list: its words in turn, in the time of one item
    orderedItems = orderedItems.flatMap(it => it.words?.length > 1
      ? it.words.map(w => ({ text: w, dur: (it.dur ?? dur) / it.words.length, _of: it })) : [it]);

    for (const [at, item] of orderedItems.entries()) {
      if (token.cancel) break;

      const text = item.text;
      const itemDur = item.dur ?? dur;

      // with a gap: the line before goes first, then the pause
      if (gap > 0 && currentOverlay) {
        const oldOverlay = currentOverlay;
        currentOverlay = null;
        oldOverlay.style.opacity = "0";
        flashOff(oldOverlay);
        await rafWait(fadeMs, token);
        oldOverlay.remove();
        await rafWait(gap * 1000, token);
        if (token.cancel) break;
      }

      // Create new overlay
      const overlay = createTextOverlay(text, uid, style, fadeMs, params.wrap !== false);
      // Only hide element on first overlay (positionOverlay handles it)
      if (!elementHidden) {
        positionOverlay(overlay, targetId, offsetX, offsetY, cueElement, uid);
        elementHidden = targetId === "self";
      } else {
        positionOverlay(overlay, targetId, offsetX, offsetY, cueElement, null);
      }

      overlay.addEventListener("click", () => {
        token.cancel = true;
      });

      document.body.appendChild(overlay);
      placeAt(params, overlay);
      startArrival(params, overlay);

      // Crossfade: fade out old, fade in new
      if (currentOverlay) {
        const oldOverlay = currentOverlay; // capture before reassignment
        oldOverlay.style.opacity = "0";
        flashOff(oldOverlay);
        setTimeout(() => {
          oldOverlay.remove();
        }, fadeMs);
      }

      await new Promise(r => requestAnimationFrame(r));
      overlay.style.opacity = "1";
      arrive(overlay);
      flashWith(overlay, params.flash);
      const t0 = performance.now(), said = sayWith(params, text);
      // the next made while this one is said
      if (params.say && orderedItems[at + 1]) prepareSaid({ ...params, mode: "next" }, [orderedItems[at + 1]]);
      currentOverlay = overlay;
      const index = items.indexOf(item._of ?? item);
      noteLine(uid, index + 1);
      if (!shuffle && !token.cancel) readTo(params, index, items.length);

      // Wait for duration (per-item or default), and for the voice
      await rafWait(itemDur * 1000, token);
      await untilSaid(said, t0, token);
    }
  }

  // If hold:true, keep final overlay visible
  if (hold && currentOverlay && !token.cancel) {
    // Register for scroll tracking
    registerHeldOverlay(uid, currentOverlay, cueElement, targetId, offsetX, offsetY, shuffle ? "shuffle" : "cycle");
    return; // Exit without removing - overlay stays on screen
  }

  // Final fade out
  flashOff(currentOverlay);
  if (currentOverlay && !token.cancel) {
    currentOverlay.style.opacity = "0";
    await rafWait(fadeMs, token);
    currentOverlay.remove();
  }
  // Restore hidden cue element if target:self was used
  showCueElement(uid);
}

/* ------------------------------------------------------------------------
   6b) Reveal Mode — lines fade in one by one and stay, then the block fades out
-------------------------------------------------------------------------*/

const SVG_NS = "http://www.w3.org/2000/svg";

// "color:#000;font-size:1.4px;line-height:1.5" -> { color: "#000", ... }
function parseStyleDecls(style) {
  const out = {};
  for (const decl of String(style || "").split(";")) {
    const i = decl.indexOf(":");
    if (i > 0) out[decl.slice(0, i).trim().toLowerCase()] = decl.slice(i + 1).trim();
  }
  return out;
}

// Fade an SVG element's opacity attribute frame by frame. SVG opacity is
// repainted as vectors each frame, where a CSS opacity transition may get
// rasterised to a texture and stretched (blurry text while it fades).
function fadeSvg(el, from, to, ms, token) {
  return new Promise((resolve) => {
    const start = performance.now();
    function step(t) {
      if (token?.cancel) return resolve();
      const k = ms > 0 ? Math.min(1, (t - start) / ms) : 1;
      const eased = 0.5 - Math.cos(Math.PI * k) / 2;   // easeInOutSine
      el.setAttribute("opacity", String(from + (to - from) * eased));
      if (k < 1) requestAnimationFrame(step); else resolve();
    }
    requestAnimationFrame(step);
  });
}

// Lay one reveal item out as native SVG <text> lines inside `g`, wrapped to
// `width` score units. Returns the y of the next line's top.
function layoutSvgRevealItem(g, text, top, width, fontSize, lineHeight, align, wrap = true) {
  const anchorX = align === "center" ? width / 2 : align === "right" || align === "end" ? width : 0;
  const anchor = anchorX === 0 ? "start" : anchorX === width ? "end" : "middle";
  // first baseline sits roughly where CSS would put it: half-leading + ascent
  const baseline = (lineHeight - fontSize) / 2 + fontSize * 0.8;
  const addLine = (str) => {
    const t = document.createElementNS(SVG_NS, "text");
    t.setAttribute("x", String(anchorX));
    t.setAttribute("y", String(top + baseline));
    t.setAttribute("text-anchor", anchor);
    t.setAttribute("xml:space", "preserve");
    t.textContent = str;
    g.appendChild(t);
    top += lineHeight;
    return t;
  };
  for (const para of String(text).split("\n")) {
    // wrap:0 — the written line IS the line. No measuring, no folding.
    if (!wrap) { addLine(para); continue; }
    const words = para.trim().split(/\s+/).filter(Boolean);
    if (!words.length) { top += lineHeight; continue; }
    let t = addLine(words[0]);
    for (const word of words.slice(1)) {
      const prev = t.textContent;
      t.textContent = `${prev} ${word}`;
      if (t.getComputedTextLength() > width) {
        t.textContent = prev;
        t = addLine(word);
      }
    }
  }
  return top;
}

// The fixed layer screen:1 reveal blocks are held in (see runSvgReveal)
function screenLayer() {
  let layer = document.getElementById("oscilla-text-screen-layer");
  if (!layer) {
    layer = document.createElementNS(SVG_NS, "svg");
    layer.id = "oscilla-text-screen-layer";
    layer.style.cssText = "position:fixed;left:0;top:0;width:100vw;height:100vh;"
      + "overflow:visible;pointer-events:none;z-index:99990";
    document.body.appendChild(layer);
  }
  return layer;
}

async function runSvgReveal(params, items, token, anchor, host) {
  const { uid, style, fadeMs, dur, offsetX, offsetY, linger, fadeOut, width } = params;

  // anchor's box in the host group's own coordinates
  const inv = host.getScreenCTM()?.inverse();
  const r = anchor.getBoundingClientRect();
  const tl = new DOMPoint(r.left, r.top).matrixTransform(inv);
  const br = new DOMPoint(r.right, r.bottom).matrixTransform(inv);
  let x = Math.min(tl.x, br.x) + offsetX;
  const y = Math.max(tl.y, br.y) + offsetY;
  if (params.left != null) {
    const view = (window.scoreContainer || document.getElementById("scoreContainer"))?.getBoundingClientRect();
    if (view && inv) x = new DOMPoint(view.left + view.width * params.left, r.top).matrixTransform(inv).x;
  }

  // style: sizes are score units, as for the HTML block
  const css = parseStyleDecls(style);
  const fontSize = parseFloat(css["font-size"]) || 12;
  const lh = css["line-height"] || "1.4";
  const lineHeight = /px$/.test(lh) ? parseFloat(lh)
    : (parseFloat(lh) || 1.4) * (/em$/.test(lh) ? fontSize : /%$/.test(lh) ? fontSize / 100 : fontSize);
  const align = css["text-align"] || "left";

  const block = document.createElementNS(SVG_NS, "g");
  block.setAttribute("class", "cue-text-reveal");
  block.dataset.uid = uid;
  block.setAttribute("transform", `translate(${x},${y})`);
  block.setAttribute("fill", css.color || css.fill || "#000");
  block.setAttribute("font-size", String(fontSize));
  block.setAttribute("font-family", css["font-family"] || window.textFont || "sans-serif");
  for (const prop of ["font-weight", "font-style", "letter-spacing", "word-spacing", "font-variant"]) {
    if (css[prop]) block.style.setProperty(prop, css[prop]);
  }
  block.style.cursor = "pointer";
  block.addEventListener("click", () => { token.cancel = true; });
  host.appendChild(block);

  // screen:1 — the block moves into a fixed overlay layer, at the matrix it
  // has on screen now, so nothing the view does (zoom, pan, a turning or
  // pinned host) can move it or make it lag a frame behind: it stays exactly
  // where it appeared, same size, still vector text
  if (params.screen) {
    const m = block.getScreenCTM();
    if (m) {
      block.setAttribute("transform", `matrix(${m.a},${m.b},${m.c},${m.d},${m.e},${m.f})`);
      block.style.pointerEvents = "auto";
      screenLayer().appendChild(block);
    }
  }

  let top = 0;
  for (const item of items) {
    if (token.cancel) break;
    const line = document.createElementNS(SVG_NS, "g");
    line.setAttribute("opacity", "0");
    block.appendChild(line);
    top = layoutSvgRevealItem(line, item.text, top, width, fontSize, lineHeight, align, params.wrap !== false);
    fadeSvg(line, 0, 1, fadeMs, token);
    noteLine(uid, items.indexOf(item) + 1);
    await rafWait((item.dur ?? dur) * 1000, token);
  }

  if (!token.cancel) await rafWait(linger * 1000, token);
  if (!token.cancel) await fadeSvg(block, 1, 0, fadeOut * 1000, token);
  block.remove();
  showCueElement(uid);
}

async function runRevealMode(params, items, token, cueElement) {
  const { uid, style, fadeMs, dur, targetId, offsetX, offsetY, linger, fadeOut } = params;

  // At an element the block is drawn inside the score as native SVG text in
  // the element's group, so it stays locked to it through zoom, pan, scrolling
  // and any transform of that group, and stays sharp at any zoom.
  // Centred on screen it is a page overlay.
  const anchor = targetId === "self" ? cueElement
    : (targetId && targetId !== "center" ? document.getElementById(targetId) : null);
  const named = params.host ? document.getElementById(params.host) : null;
  if (params.host && !named) console.warn(`[text] host:${params.host} — no such element, using the target's own group`);
  const host = anchor?.ownerSVGElement
    ? ((named?.ownerSVGElement ? named : null) || anchor.closest("g") || anchor.ownerSVGElement) : null;
  if (host) return runSvgReveal(params, items, token, anchor, host);

  const block = createTextOverlay("", uid, style, fadeMs, params.wrap !== false);
  block.style.opacity = "1";
  block.style.whiteSpace = "normal";
  positionOverlay(block, targetId, offsetX, offsetY, cueElement, uid);
  document.body.appendChild(block);
  block.addEventListener("click", () => { token.cancel = true; });

  for (const item of items) {
    if (token.cancel) break;
    const line = document.createElement("div");
    line.textContent = item.text;
    line.style.opacity = "0";
    line.style.transition = `opacity ${fadeMs}ms ease`;
    line.style.whiteSpace = params.wrap === false ? "pre" : "pre-line";
    block.appendChild(line);
    await new Promise(r => requestAnimationFrame(r));
    line.style.opacity = "1";
    noteLine(uid, items.indexOf(item) + 1);
    await rafWait((item.dur ?? dur) * 1000, token);
  }

  if (!token.cancel) await rafWait(linger * 1000, token);

  block.style.transition = `opacity ${fadeOut * 1000}ms ease`;
  block.style.opacity = "0";
  await rafWait(fadeOut * 1000, token);
  block.remove();
  showCueElement(uid);
}

/* ------------------------------------------------------------------------
   7) Main Handler
-------------------------------------------------------------------------*/

// every text run still going, by its token -- stopAllCueTexts cancels them all
const liveTokens = new Set();

/**
 * Main handler for text cues
 * @param {Object} ast - Parsed AST
 * @param {Element} cueElement - The SVG element that triggered this cue
 * @param {Object} networkData - Optional data from network sync { fromNetwork, selectedIndex }
 */
export async function handleCueTextFromAST(ast, cueElement = null, networkData = {}) {
  try {
    const params = parseTextParams(ast, cueElement);
    const { fromNetwork = false, selectedIndex = null, shuffledSequence = null } = networkData;
    setWordLook(params.uid, params);
    params.live = !cueElement;          // a console line (readFrom: it reads on when run again)

    // mask:spot / hole:spot -- the text seen only inside a shape, or with it cut out
    // (stageMask.js); a line with nothing else -- text(uid:w, mask:off) -- does only that
    const maskArg = k => ast?.args?.find(a => a.type === k)?.value;
    const masked = params.uid != null && maskFromArgs(`text:${params.uid}`, () => document.querySelectorAll(`[data-uid="${CSS.escape(String(params.uid))}"]`), maskArg);
    // along:star / along:bass -- the text running round a shape's outline or along a sound's trace
    // (textAlong.js), crawling at speed: px a second
    const alongTo = maskArg("along");
    if (alongTo != null && params.uid != null) setAlong(String(params.uid), alongTo, Number(maskArg("speed")) || 0);
    if (!params.mode || !params.content) {
      if (!masked && alongTo == null) console.warn("[cueText] No mode or content specified");
      return;
    }
    // at:[x, y] -- this text stands there from now on, as viz(uid, at:) puts it
    const atp = atPair(params.at);   // at:0.5 is at:[0.5, 0.5]
    if (Array.isArray(atp) && atp.length >= 2 && atp.map(Number).every(Number.isFinite)) {
      placeText(params.uid, Number(atp[0]), Number(atp[1]));
    }
    const textOpts = loadOptsOf(params);
    // stay:1 run by a signal, or a pick: one more item, and all those before
    // it stay -- nothing is cleared or cancelled
    if (params.stay && (params.mode === "pick" || (params.stepped && (params.mode === "cycle" || params.mode === "shuffle")))) {
      const items = await loadContent(params.content, params.unit, textOpts);
      if (!items.length) { console.warn("[cueText] No content to display"); return; }
      prepareSaid(params, items);
      const i = params.mode === "pick" ? Math.floor(Math.random() * items.length)
        : (nextStep(params.uid, items.length, params.mode === "shuffle") + fromAt(params, items.length)) % items.length;
      noteLine(params.uid, i + 1);
      await stayOne(params, items[i], cueElement);
      return;
    }
    noteLine(params.uid, 0);
    noteEnd(params.uid, 0);

    // Remove any preview before starting the actual cue
    removePreview(params.uid);

    // Remove any existing overlays with same UID (including held content from
    // previous runs) -- with stay:1, those still coming in land as well
    if (!params.stay) removeOverlaysByUid(params.uid);

    // Cancel any existing active instance with same UID -- and this one's
    // token in its place now, before the content loads: taken after, two
    // runs close together each found none to cancel, and the first ran on
    // with nothing able to stop it (Ctrl+, nor a run again)
    if (!window.activeCueTexts) window.activeCueTexts = new Map();
    const existing = window.activeCueTexts.get(params.uid);
    if (existing) {
      existing.cancel = true;
      window.activeCueTexts.delete(params.uid);
    }
    const token = { cancel: false, uid: params.uid };
    window.activeCueTexts.set(params.uid, token);
    liveTokens.add(token);

    // Load content
    const items = await loadContent(params.content, params.unit, loadOptsOf(params));
    if (token.cancel) { liveTokens.delete(token); return; }
    if (!items.length) {
      console.warn("[cueText] No content to display");
      liveTokens.delete(token);
      if (window.activeCueTexts.get(params.uid) === token) window.activeCueTexts.delete(params.uid);
      return;
    }
    prepareSaid(params, items);

    const loopCount = loopCountOf(params.loop);     // for the sequence

    // For pick mode with sync, determine the selected index
    let pickIndex = selectedIndex;
    if (params.mode === "pick" && params.sync && !fromNetwork) {
      pickIndex = Math.floor(Math.random() * items.length);
    }

    // For shuffle mode with sync, generate shuffled sequence
    let syncShuffledSequence = shuffledSequence;
    if (params.mode === "shuffle" && params.sync && !fromNetwork) {
      syncShuffledSequence = generateShuffledSequence(items.length, loopCount);
    }

    // Broadcast to other clients if not from network
    if (!fromNetwork && window.socket?.readyState === WebSocket.OPEN) {
      const message = {
        type: "cueText",
        uid: params.uid,
        cueId: cueElement?.id || params.uid,
        // For synced pick mode, include the selected index
        selectedIndex: (params.mode === "pick" && params.sync) ? pickIndex : null,
        // For synced shuffle mode, include the shuffled sequence
        shuffledSequence: (params.mode === "shuffle" && params.sync) ? syncShuffledSequence : null,
      };
      window.socket.send(JSON.stringify(message));
    }

    // cycle / shuffle run by a signal (trig:beat.div(4)): one item a hit, the
    // next each time -- through a list, or a .csv of thousands, on the beat
    if (params.stepped && (params.mode === "cycle" || params.mode === "shuffle")) {
      // stepped fast (trig:beat.div(4)): the fade no more than a quarter of the time between steps
      const t = performance.now(), last = lastStepAt.get(params.uid);
      lastStepAt.set(params.uid, t);
      if (!params.fadeGiven && last != null && t - last < 4000) params.fadeMs = Math.min(params.fadeMs, (t - last) * 0.25);
      await runPickMode(params, items, token, cueElement, (nextStep(params.uid, items.length, params.mode === "shuffle") + fromAt(params, items.length)) % items.length);
      return;
    }

    // stay:1 -- a cycle / shuffle with no trig: one each dur:, and they stay
    const stayRun = params.stay && (params.mode === "cycle" || params.mode === "shuffle");
    if (stayRun) await runStayMode(params, items, token, cueElement);

    // Run appropriate mode
    else switch (params.mode) {
      case "pick":
        // Use synced index if available (either from network or locally generated for broadcast)
        const useIndex = params.sync ? (fromNetwork ? selectedIndex : pickIndex) : null;
        await runPickMode(params, items, token, cueElement, useIndex);
        break;
      case "scroll":
        await runScrollMode(params, items, token, cueElement);
        break;
      case "ticker":
        await runTickerMode(params, items, token, cueElement);
        break;
      case "reveal":
        await runRevealMode(params, items, token, cueElement);
        break;
      case "shuffle":
        // Use synced sequence if available (either from network or locally generated)
        const useSequence = params.sync ? (fromNetwork ? shuffledSequence : syncShuffledSequence) : null;
        await runCycleMode(params, items, token, cueElement, true, useSequence);
        break;
      case "cycle":
      default:
        await runCycleMode(params, items, token, cueElement, false);
        break;
    }

    // Cleanup (skip if hold:true - overlay stays visible; with stay:1 the
    // last ones are still coming in)
    if (!params.hold && !stayRun) {
      removeOverlaysByUid(params.uid);
    }
    if (window.activeCueTexts.get(params.uid) === token || !window.activeCueTexts.has(params.uid)) {
      noteLine(params.uid, 0);
      if (!token.cancel) noteEnd(params.uid, 1);
    }
    if (window.activeCueTexts.get(params.uid) === token) window.activeCueTexts.delete(params.uid);
    liveTokens.delete(token);

    // read to the end: the next run from the top
    if (!token.cancel) readPlaces.delete(params.uid);
    // next: — run the follow-on cue, unless this text was stopped or replaced
    if (!token.cancel) runNext(params.next, cueElement);

  } catch (err) {
    console.error("[cueText] Error:", err);
  }
}

/* ------------------------------------------------------------------------
   8) Global Controls
-------------------------------------------------------------------------*/

export function stopAllCueTexts() {
  const uids = new Set([...stayLayers.keys(), ...stayGens.keys()]);
  for (const uid of uids) clearStay(uid);
  rowY = FLOW_MARGIN;
  // every run still going -- one no longer in activeCueTexts too (replaced
  // by a later run of its uid before it was cancelled)
  for (const token of liveTokens) { token.cancel = true; if (token.uid) uids.add(token.uid); }
  liveTokens.clear();
  for (const [uid, token] of window.activeCueTexts?.entries() || []) {
    token.cancel = true;
    uids.add(uid);
    removeOverlaysByUid(uid);
  }
  window.activeCueTexts?.clear();
  // the hold:true previews and the held overlays, of every kind (scroll and ticker too)
  for (const map of [window._textCuePreviews, window._textCueHeldOverlays]) {
    for (const data of map?.values() || []) data.overlay?.remove();
    map?.clear();
  }
  // and the words on their way in: a stay:1 one fired on a signal is in no token
  // (and the boxes a scroll, a ticker or a reveal stands in: held ones are in no token either)
  for (const el of document.querySelectorAll(".cue-text-overlay, .cue-text-scroll-container, .cue-text-ticker-container, .cue-text-reveal")) { if (el.dataset.uid) uids.add(el.dataset.uid); el.remove(); }
  // what they were saying, and any phrase still being made for them
  if (uids.size) import("./say.js").then(m => m.silenceSpeech([...uids])).catch(() => {});
}
onReset("stage", stopAllCueTexts);

/** Stop one text by uid: its reveal ends and its block goes, at once (and all it left with stay:1). */
export function stopCueText(uid) {
  noteLine(uid, 0);
  readPlaces.delete(uid);
  clearStay(uid);
  for (const token of liveTokens) if (token.uid === uid) { token.cancel = true; liveTokens.delete(token); }
  const token = window.activeCueTexts?.get(uid);
  if (token) {
    token.cancel = true;
    window.activeCueTexts.delete(uid);
  }
  removeOverlaysByUid(uid);
  import("./say.js").then(m => m.silenceSpeech([uid])).catch(() => {});
}

/* ------------------------------------------------------------------------
   9) Network Sync Handler
-------------------------------------------------------------------------*/

/**
 * Handle incoming text cue from network
 * @param {Object} data - { cueId, uid, selectedIndex, shuffledSequence }
 */
export function handleCueTextFromNetwork(data) {
  const { cueId, selectedIndex, shuffledSequence } = data;

  const networkData = {
    fromNetwork: true,
    selectedIndex: selectedIndex,
    shuffledSequence: shuffledSequence,
  };

  // Find the cue element by ID
  const cueElement = document.getElementById(cueId);

  // Try click-triggered cue registry first
  const registryEntry = window._textCueRegistry?.get(cueId);
  if (registryEntry?.ast) {
    handleCueTextFromAST(registryEntry.ast, registryEntry.element || cueElement, networkData);
    return;
  }

  // Try window.cues array for playhead-triggered cues
  if (cueElement) {
    const cueEntry = window.cues?.find(c => c.element === cueElement);
    if (cueEntry?.ast) {
      handleCueTextFromAST(cueEntry.ast, cueElement, networkData);
      return;
    }
  }

  // Try finding by matching the cueId in the id field
  const cueEntryById = window.cues?.find(c => c.id === cueId || c.element?.id === cueId);
  if (cueEntryById?.ast) {
    handleCueTextFromAST(cueEntryById.ast, cueEntryById.element, networkData);
    return;
  }

  console.warn(`[cueText] Network: no AST found for: ${cueId}`);
}

/* ------------------------------------------------------------------------
   10) Preview Display
-------------------------------------------------------------------------*/

// Store preview overlays for cleanup (with element references for scroll tracking)
if (!window._textCuePreviews) window._textCuePreviews = new Map();
// Store held overlays for scroll tracking
if (!window._textCueHeldOverlays) window._textCueHeldOverlays = new Map();

/**
 * Update position of a tracked overlay based on its source element
 */
function updateOverlayPosition(data) {
  if (!data.cueElement || !data.overlay || data.targetId !== "self") return;

  const box = data.cueElement.getBoundingClientRect();
  const offsetX = data.offsetX || 0;
  const offsetY = data.offsetY || 0;

  if (data.mode === "ticker" || data.mode === "scroll") {
    // Container-based positioning (top-left)
    data.overlay.style.left = `${box.x + offsetX}px`;
    data.overlay.style.top = `${box.y + offsetY}px`;
    data.overlay.style.width = `${box.width}px`;
    data.overlay.style.height = `${box.height}px`;
  } else {
    // Center-based positioning for pick/cycle/shuffle
    data.overlay.style.left = `${box.x + box.width / 2 + offsetX}px`;
    data.overlay.style.top = `${box.y + box.height / 2 + offsetY}px`;
  }
}

// Continuous position tracking using requestAnimationFrame
if (!window._textCuePositionTracker) {
  let isTracking = false;

  const trackPositions = () => {
    // previews and held overlays follow their elements; one whose overlay
    // or element has left the page is let go, so the loop can end
    for (const map of [window._textCuePreviews, window._textCueHeldOverlays]) {
      for (const [uid, data] of map) {
        if (!data.overlay?.isConnected || (data.cueElement && !data.cueElement.isConnected)) { data.overlay?.remove(); map.delete(uid); continue; }
        updateOverlayPosition(data);
      }
    }

    // Continue tracking if there are any overlays to track
    if (window._textCuePreviews.size > 0 || window._textCueHeldOverlays.size > 0) {
      requestAnimationFrame(trackPositions);
    } else {
      isTracking = false;
    }
  };

  window._textCuePositionTracker = {
    start: () => {
      if (!isTracking) {
        isTracking = true;
        requestAnimationFrame(trackPositions);
      }
    }
  };
}

/**
 * Register a held overlay for scroll tracking
 */
function registerHeldOverlay(uid, overlay, cueElement, targetId, offsetX, offsetY, mode) {
  window._textCueHeldOverlays.set(uid, {
    overlay,
    cueElement,
    targetId,
    offsetX: offsetX || 0,
    offsetY: offsetY || 0,
    mode
  });
  // Start position tracking
  window._textCuePositionTracker.start();
}

/**
 * Create and display a static preview of the first item
 * Called during registration for cues with hold:true
 */
async function showPreview(cueElement, ast) {
  const params = parseTextParams(ast, cueElement);
  if (!params.hold) return null;
  if (!params.mode || !params.content) return null;

  // Load content to get first item
  const items = await loadContent(params.content, params.unit, loadOptsOf(params));
  if (!items.length) return null;

  const { uid, style, fadeMs, targetId, offsetX, offsetY, pad } = params;

  // For ticker mode, show the full text statically
  if (params.mode === "ticker") {
    const textContent = items.map(item => item.text).join("  •  ");
    let boundingBox = null;
    const padding = Number(pad) || 16;

    if (targetId === "self" && cueElement) {
      const box = cueElement.getBoundingClientRect();
      boundingBox = {
        x: box.x + offsetX,
        y: box.y + offsetY,
        width: box.width,
        height: box.height,
      };
    }

    const { container, inner } = createTickerContainer(textContent, uid + "_preview", style, boundingBox, padding);
    container.dataset.preview = "true";

    // Position text at start (visible, not scrolled off)
    inner.style.paddingLeft = "0";
    inner.style.transform = "translateX(0)";

    document.body.appendChild(container);
    container.style.opacity = "0";
    container.style.transition = "opacity 300ms ease";
    await new Promise(r => requestAnimationFrame(r));
    container.style.opacity = "1";

    // Store with element reference for scroll tracking
    const previewData = {
      overlay: container,
      cueElement,
      targetId,
      offsetX,
      offsetY,
      mode: "ticker"
    };
    removePreview(uid);              // one preview a uid: a re-registration replaces it
    window._textCuePreviews.set(uid, previewData);
    // Immediate position update, then start continuous tracking
    updateOverlayPosition(previewData);
    window._textCuePositionTracker.start();
    return container;
  }

  // For scroll mode, show first few lines statically
  if (params.mode === "scroll") {
    const previewItems = items.slice(0, 3); // Show first 3 lines as preview
    let boundingBox = null;
    const padding = Number(pad) || 16;

    if (targetId === "self" && cueElement) {
      const box = cueElement.getBoundingClientRect();
      boundingBox = {
        x: box.x + offsetX,
        y: box.y + offsetY,
        width: box.width,
        height: box.height,
      };
    }

    const { container, inner } = createScrollContainer(previewItems, uid + "_preview", style, params.speed, boundingBox, padding, "", params.wrap !== false);
    container.dataset.preview = "true";

    // Don't scroll - show at top
    inner.style.transform = "translateY(0)";
    inner.style.transition = "none";

    document.body.appendChild(container);
    container.style.opacity = "0";
    container.style.transition = "opacity 300ms ease";
    await new Promise(r => requestAnimationFrame(r));
    container.style.opacity = "1";

    // Store with element reference for scroll tracking
    const previewData = {
      overlay: container,
      cueElement,
      targetId,
      offsetX,
      offsetY,
      mode: "scroll"
    };
    removePreview(uid);              // one preview a uid: a re-registration replaces it
    window._textCuePreviews.set(uid, previewData);
    // Immediate position update, then start continuous tracking
    updateOverlayPosition(previewData);
    window._textCuePositionTracker.start();
    return container;
  }

  // For pick/cycle/shuffle, show first item
  const firstItem = items[0];
  const overlay = createTextOverlay(firstItem.text, uid + "_preview", style, fadeMs, params.wrap !== false);
  overlay.dataset.preview = "true";
  positionOverlay(overlay, targetId, offsetX, offsetY, cueElement, null); // Don't hide cue element for preview

  document.body.appendChild(overlay);
  await new Promise(r => requestAnimationFrame(r));
  overlay.style.opacity = "1";

  // Store with element reference for scroll tracking
  const previewData = {
    overlay,
    cueElement,
    targetId,
    offsetX,
    offsetY,
    mode: params.mode
  };
  removePreview(uid);              // one preview a uid: a re-registration replaces it
  window._textCuePreviews.set(uid, previewData);
  // Immediate position update, then start continuous tracking
  updateOverlayPosition(previewData);
  window._textCuePositionTracker.start();
  return overlay;
}

/**
 * Remove preview overlay when cue starts (instant removal)
 */
function removePreview(uid) {
  const data = window._textCuePreviews?.get(uid);
  if (data) {
    data.overlay.remove();
    window._textCuePreviews.delete(uid);
  }
}

/* ------------------------------------------------------------------------
   11) Trigger Registration
-------------------------------------------------------------------------*/

// Store AST for click-triggered cues (for network sync lookup)
if (!window._textCueRegistry) window._textCueRegistry = new Map();

/**
 * Register playhead trigger - shows preview if hold:true
 * Called from cueDispatcher for standard playhead-triggered cues
 * @param {Element} cueElement - The SVG element
 * @param {Object} ast - The parsed AST
 */
export function registerPlayheadTrigger(cueElement, ast) {
  if (!cueElement) return;

  // Store AST for lookup
  window._textCueRegistry.set(cueElement.id, { element: cueElement, ast });

  // Show preview if hold:true
  const params = parseTextParams(ast, cueElement);
  if (params.hold) {
    showPreview(cueElement, ast);
  }
}

/**
 * Register click trigger for a text cue element
 * Called from cueDispatcher when trig:click is detected
 * @param {Element} cueElement - The SVG element
 * @param {Object} ast - The parsed AST
 */
export function registerClickTrigger(cueElement, ast) {
  if (!cueElement) return;

  // Store AST for network lookup
  window._textCueRegistry.set(cueElement.id, { element: cueElement, ast });

  // Mark element as clickable - must set pointer-events for SVG elements
  cueElement.style.cursor = "pointer";
  cueElement.style.pointerEvents = "all";
  // Tag so the transport-touch pointer capture excludes this element —
  // without it the capture retargets the click and it never arrives.
  if (cueElement.dataset) cueElement.dataset.cueClickable = "1";

  // Show preview if hold:true
  const params = parseTextParams(ast, cueElement);
  if (params.hold) {
    showPreview(cueElement, ast);
  }

  // Remove existing listener if any
  const existingHandler = cueElement._textClickHandler;
  if (existingHandler) {
    cueElement.removeEventListener("click", existingHandler);
  }

  // Add click handler
  const handler = (e) => {
    e.stopPropagation();
    // Remove preview before starting cue
    removePreview(params.uid);
    handleCueTextFromAST(ast, cueElement);
  };

  cueElement._textClickHandler = handler;
  cueElement.addEventListener("click", handler);
}

/**
 * Register toggle trigger - click to start, click again to stop
 * @param {Element} cueElement - The SVG element
 * @param {Object} ast - The parsed AST
 */
export function registerToggleTrigger(cueElement, ast) {
  if (!cueElement) return;

  // Store AST for network lookup
  window._textCueRegistry.set(cueElement.id, { element: cueElement, ast });

  // Mark element as clickable
  cueElement.style.cursor = "pointer";
  cueElement.style.pointerEvents = "all";
  if (cueElement.dataset) cueElement.dataset.cueClickable = "1";

  // Show preview if hold:true
  const params = parseTextParams(ast, cueElement);
  if (params.hold) {
    showPreview(cueElement, ast);
  }

  // Track running state
  let isRunning = false;

  // Remove existing listener if any
  const existingHandler = cueElement._textToggleHandler;
  if (existingHandler) {
    cueElement.removeEventListener("click", existingHandler);
  }

  const handler = (e) => {
    e.stopPropagation();

    if (isRunning) {
      // Stop the running cue
      const token = window.activeCueTexts?.get(params.uid);
      if (token) {
        token.cancel = true;
      }
      removeOverlaysByUid(params.uid);
      isRunning = false;
      cueElement.style.opacity = "1"; // Restore element visibility
      // Restore preview if hold:true
      if (params.hold) {
        showPreview(cueElement, ast);
      }
    } else {
      // Start the cue
      // Remove preview before starting cue
      removePreview(params.uid);
      isRunning = true;
      handleCueTextFromAST(ast, cueElement).then(() => {
        isRunning = false; // Reset when naturally completes
      });
    }
  };

  cueElement._textToggleHandler = handler;
  cueElement.addEventListener("click", handler);
}

/**
 * Register extent trigger - runs while playhead is within element bounds
 * @param {Element} cueElement - The SVG element
 * @param {Object} ast - The parsed AST
 */
export function registerExtentTrigger(cueElement, ast) {
  if (!cueElement) return;

  // Store AST for network lookup
  window._textCueRegistry.set(cueElement.id, { element: cueElement, ast });

  // Track state
  let isRunning = false;
  let checkInterval = null;

  const params = parseTextParams(ast, cueElement);

  // Show preview if hold:true
  if (params.hold) {
    showPreview(cueElement, ast);
  }

  const checkPlayheadPosition = () => {
    const playhead = document.getElementById("playhead");
    if (!playhead) return;

    const playheadRect = playhead.getBoundingClientRect();
    const elementRect = cueElement.getBoundingClientRect();

    // Check if playhead x is within element bounds
    const playheadX = playheadRect.left + playheadRect.width / 2;
    const inBounds = playheadX >= elementRect.left && playheadX <= elementRect.right;

    if (inBounds && !isRunning) {
      // Start
      // Remove preview before starting cue
      removePreview(params.uid);
      isRunning = true;
      handleCueTextFromAST(ast, cueElement);
    } else if (!inBounds && isRunning) {
      // Stop
      const token = window.activeCueTexts?.get(params.uid);
      if (token) {
        token.cancel = true;
      }
      removeOverlaysByUid(params.uid);
      showCueElement(params.uid);
      isRunning = false;
      // Restore preview if hold:true
      if (params.hold) {
        showPreview(cueElement, ast);
      }
    }
  };

  // Start checking when score loads. One check per element id: registered again (a
  // page visited again, a rescan) the last one's goes, as it does with its
  // element, and every one on a project switch (teardown.js ownerSignal)
  const signal = ownerSignal(cueElement.id || cueElement, "text-extent");
  checkInterval = setInterval(() => {
    if (!cueElement.isConnected) { clearInterval(checkInterval); return; }
    checkPlayheadPosition();
  }, 100);
  signal.addEventListener("abort", () => clearInterval(checkInterval), { once: true });

  // Store interval for cleanup
  cueElement._extentInterval = checkInterval;
}
